import _ from 'lodash';
import JSONPath from 'jsonpath';
import moment from 'moment-timezone';

import {ROLE_MAP} from 'app-ui-componentlibrary/dist/common/constants';

import PermissionChecks from 'go/security/permissions';

import {
  toStartCase,
  safeAdd,
  safeAddReduce,
  safeMult,
  compileContent,
  checkIfValuesAre,
  showDollarAmountWithDollar,
  fetchPhaseArray,
  calculateTotalFundingStreams,
  calculatePaymentDispursements,
  getBudgetItemsAtTier,
  calculatePaymentDispursementsForSubGrants,
  safeAddReturnNonNegative,
  checkIfBsirExistForReportingPeriodWithGivenStatus,
  getFirstNonNullKeyInObject,
  safeSubstract,
} from 'common/utils';
import {
  calculateChildrenTotalByMode,
  calculateManagementCostThreshold,
  sumTotalSubRecipientManagementCosts,
} from 'common/requestDetails/utils';
import {runRule} from 'framework/sagas/validationSaga';
import {
  arrAttrSum,
  validateAllSubapplications,
  getCostsTotalForAttribute,
  areCostItemsChangedBetweenFundingStreams,
} from 'common/utils';
import {
  CAN_SUBMIT,
  CAN_SUBMIT_POSITIVE,
  SECTION_RESULTS,
  FACTOR_RESULTS,
  PART_RESULTS,
  ERROR,
} from 'app-ui-componentlibrary/dist/common/constants';
import {
  OPSG,
  SHSP,
  UASI,
  SHSPFOROPSG,
  SHSPFORSHSP,
  UASIFORUASI,
  OPSGFOROPSG,
  COST,
  PERFORMING_ORG_ID_PATH,
  TOTAL,
  LOCAL_AGENCY,
  STATE_AGENCY,
} from 'common/requestDetails/constants';
import {BudgetUtils} from 'app-ui-componentlibrary/dist/go/components/common/BudgetUtils';

const setObj = (top, path, value) => _.setWith(top, path, value, Object);
const INVESTMENT = 'investment';
const toResult = (isTrue, message, header) => ({
  isValid: isTrue,
  message: message,
  header: header,
});

const isAOorSAOorGMS = (data) => {
  return (
    PermissionChecks.compareRolesAndPermissions(
      [ROLE_MAP.PGMAO.code],
      data.context.auth.permissions
    ) ||
    PermissionChecks.compareRolesAndPermissions(
      [ROLE_MAP.SAO.code],
      data.context.auth.permissions
    ) ||
    PermissionChecks.compareRolesAndPermissions(
      [ROLE_MAP.PGMGMS.code],
      data.context.auth.permissions
    )
  );
};

//GMMGO-65188 disable ROF for Safer grants.
const isAOorSAOorGMSAndROFEnabled = (data) => {
  let foConfigDataRofDisabled = Boolean(
    _.get(data, 'context.fundingOpportunityData.configData.returnOfFunds.disabled', false)
  ).valueOf();
  return foConfigDataRofDisabled ? false : isAOorSAOorGMS(data);
};

const isOneOf = ({data, message, toMatch}) => {
  const isValid = _.includes(toMatch, data);
  return toResult(isValid, message);
};

const isToggled = ({data, message, ifNot, entityType, entityId, toggle}) => {
  const fullToggle = entityType ? `${entityType}-${entityId}-${toggle}` : toggle;
  const toggleEnabled = _.includes(data, fullToggle);
  const isValid = (ifNot && !toggleEnabled) || (!ifNot && toggleEnabled);
  return toResult(isValid, message);
};

const isNotOneOf = ({data, message, toMatch}) => {
  const isValid = !_.includes(toMatch, data);
  return toResult(isValid, message);
};

const isRequired = ({data, message}) => {
  let isValid = false;
  switch (typeof data) {
    case 'string':
    case 'number':
    case 'boolean':
      isValid = !_.isEmpty(_.trim(data));
      break;
    default:
      isValid = !_.isEmpty(data);
  }
  return toResult(isValid, message);
};

const isNotEqual = ({data, values, message}) => {
  let isValid = false;
  let valuesArray = _.isArray(values) ? values : [values];
  if (!_.isEmpty(_.trim(data)) && !_.isEmpty(valuesArray)) {
    isValid = _.indexOf(valuesArray, _.trim(data)) === -1;
  }
  return toResult(isValid, message);
};

export const hasFundingStreamType = ({fundingStreams, fundingStreamType, message}) => {
  const fSType = _.find(
    fundingStreams,
    (stream) => stream.fundingStreamType === fundingStreamType
  );
  return toResult(!_.isEmpty(fSType), message || 'Is valid funding stream type');
};

export const areCostItemsTheSame = ({
  originalFundingStreams,
  updatedFundingStreams,
  message,
}) => {
  const isValid = !areCostItemsChangedBetweenFundingStreams(
    originalFundingStreams,
    updatedFundingStreams
  );
  return toResult(isValid, message);
};

export const hasAtLeastOneOfFundingStreamTypes = ({
  fundingStreams,
  fundingStreamTypes,
  message,
}) => {
  let foundTypes = [];
  _.forEach(fundingStreamTypes, (type) => {
    hasFundingStreamType({fundingStreams, fundingStreamType: type, message}).isValid &&
      foundTypes.push(type);
  });
  return toResult(!_.isEmpty(foundTypes), message || 'Is valid funding stream type');
};

const compareToSumValidation = (props) => {
  const {message} = props;
  let isValid = compareToOperation(props, 'add').valid;
  return toResult(isValid, message);
};

const compareToMultValidation = (props) => {
  const {message} = props;
  let isValid = compareToOperation(props, 'mult').valid;
  return toResult(isValid, message);
};

const compareToOperation = (props, operation) => {
  const {data, values, comparison} = props;
  let isValid = false;
  let result = 0;
  let inputNum = _.toNumber(data);
  const finalVals = _.map(values, (val) => {
    return _.toNumber(compileContent(val, props));
  });
  if (!_.isEmpty(finalVals)) {
    switch (operation) {
      case 'add':
        result = safeAdd(...finalVals);
        break;
      case 'mult':
        result = safeMult(...finalVals);
        break;
      default:
        result = safeAdd(...finalVals);
        break;
    }
    if (comparison.allowLess) {
      if (comparison.allowEqual) {
        isValid = inputNum <= result;
      } else {
        isValid = inputNum < result;
      }
    } else if (comparison.allowMore) {
      if (comparison.allowEqual) {
        isValid = inputNum >= result;
      } else {
        isValid = inputNum > result;
      }
    } else if (comparison.allowEqual) {
      isValid = inputNum === result;
    } else if (!comparison.allowEqual) {
      isValid = inputNum !== result;
    }
  }
  return {
    valid: isValid,
    result: result,
  };
};

const subApplicationValidations = (props) => {
  const {applicationLinksData, categoryLabel, message} = props;
  const validationProps = {
    applicationLinksData: _.get(applicationLinksData, 'data'),
    categoryLabel: categoryLabel,
  };

  const errors = validateAllSubapplications(validationProps);

  return {
    isValid: _.size(errors) === 0,
    message: message,
    subAppValidationResults: errors,
  };
};

const sumIsEqualTo = (props) => {
  const {message, sumValue, sumOnly} = props;
  const {valid, result} = compareToOperation(props, 'add');
  let isValid = false;

  //When using sumOnly flag, this rule will only evaluate the valid flag in this method,
  //else it will use both valid flags from the compareToOperation()
  let sumValid = sumValue === result;
  if (sumValid) {
    if (sumOnly) {
      isValid = sumValid;
    } else {
      isValid = sumValid && valid;
    }
  } else if (!sumOnly) {
    isValid = true;
  }
  return toResult(isValid, message);
};

const isPositiveNumber = ({data, message}) => {
  return toResult(_.toNumber(data) >= 0, message);
};

const isPositiveWholeNumber = ({data, message}) => {
  let number = _.toNumber(data);
  let onlyNumbers = new RegExp('^[0-9]+$');
  return toResult(number >= 0 && onlyNumbers.test(data) && _.isInteger(number), message);
};

const isPositiveWholeNumberOrEmpty = ({data, message}) => {
  let number = _.toNumber(data);
  return toResult(
    (number >= 0 && _.isInteger(number)) || checkIfValuesAre([null, undefined, ''], data),
    message
  );
};

const maxLength = ({data, message, max}) => {
  return toResult(_.isEmpty(_.toString(data)) || _.toString(data).length <= max, message);
};

const lengthEquals = ({data, message, length}) => {
  return toResult(
    _.isEmpty(_.toString(data)) || _.toString(data).length == length,
    message
  );
};

const minLength = ({data, message, min}) => {
  let isValid = _.size(data) >= min;
  return toResult(isValid, message);
};

const regexMatch = ({data, message, regex}) => {
  let isValid = !!(_.isString(data) && data.match(regex));
  return toResult(isValid, message);
};

const isGreaterThanValue = ({data, message, value}) => {
  let isValid = data > value;
  return toResult(isValid, message);
};

const isGreaterThanOrEqualToValue = ({data, message, value}) => {
  let isValid = data >= value;
  return toResult(isValid, message);
};

const isLessThanOrEqualToValue = ({data, message, value}) => {
  let isValid = data <= _.toNumber(value);
  return toResult(isValid, message);
};

const compareArraySums = ({
  data,
  message,
  value,
  defaultValueIfUndefined,
  comparison,
}) => {
  const sum1 = _.sumBy(data, (n) => {
    return _.toNumber(n);
  });
  const sum2 = _.sumBy(value, (n) => {
    return _.toNumber(n);
  });
  let result = isCompareValue({
    data: sum1,
    message,
    value: sum2,
    defaultValueIfUndefined,
    comparison,
  });
  return result;
};

const isCompareValue = ({data, message, value, defaultValueIfUndefined, comparison}) => {
  let isValid = false;
  let inputNum = _.toNumber(data);
  let inputValue = !_.isNaN(_.toNumber(value))
    ? _.toNumber(value)
    : _.toNumber(defaultValueIfUndefined);

  if (comparison.allowLess) {
    if (comparison.allowEqual) {
      isValid = inputNum <= inputValue;
    } else {
      isValid = inputNum < inputValue;
    }
  } else if (comparison.allowMore) {
    if (comparison.allowEqual) {
      isValid = inputNum >= inputValue;
    } else {
      isValid = inputNum > inputValue;
    }
  } else if (comparison.allowEqual) {
    isValid = inputNum === inputValue;
  } else if (!comparison.allowEqual) {
    isValid = inputNum !== inputValue;
  }
  return toResult(isValid, message);
};

const isCompareSubtractedValue = ({data, message, value, subtract, comparison}) => {
  const subtractedValue = _.subtract(value, subtract);
  return isCompareValue({data, message, value: subtractedValue, comparison});
};

const isEmpty = ({data, message}) => {
  let empty = _.isNumber(data) ? false : _.isEmpty(data);
  return toResult(empty, message);
};

const isNotEmpty = ({data, message}) => {
  let notEmpty = _.isNumber(data) ? true : !_.isEmpty(data);
  return toResult(notEmpty, message);
};

const nonFederalSources = ({data}) => {
  const nonFederal = _.get(data, 'nonFederal', {});
  const actualAppShare = _.get(data, 'share.actualAppShare');
  let applicantPeriodTotals = 0;
  const {applicant, state, other} = nonFederal;
  const nonFederalTotal = safeAddReturnNonNegative(applicant, state, other);
  !actualAppShare
    ? (applicantPeriodTotals = _.get(data, 'share.appShare'))
    : (applicantPeriodTotals = _.get(data, 'share.actualAppShare'));
  const applicantShareTotal = _.reduce(
    applicantPeriodTotals,
    (acc, applicantPeriodTotal) => safeAddReturnNonNegative(acc, applicantPeriodTotal)
  );

  return {
    isValid: _.isEqual(_.toNumber(nonFederalTotal), _.toNumber(applicantShareTotal)),
    message: `The combined Non-federal resources (Applicant + State + Other sources) must equal the overall total Non-federal resources of ${showDollarAmountWithDollar(
      applicantShareTotal
    )}.`,
  };
};

const costEstimatePhasedProjectRule = ({scopeOfWork, fundingStreams, value}) => {
  const {phase1, phase2, notApplicable} = value;
  const {phasedProject} = scopeOfWork;
  const isPhasedProject = phasedProject;

  let phaseArray = fetchPhaseArray(fundingStreams);
  let isValid = false;

  if (isPhasedProject === 'Yes' && phaseArray.length > 0) {
    if (phaseArray.includes(phase1) && phaseArray.includes(phase2)) {
      isValid = true;
    }
  }
  return toResult(isValid, null);
};
const managementCostMaxThresholdRule = ({data, config, costshareFS, appOrgId}) => {
  const fundingStreams = config.fundingStreams;
  const managementCostResultObj = calculateManagementCostThreshold({
    data,
    fundingStreams,
    costshareFS,
    appOrgId,
  });
  const managementTotalCost = managementCostResultObj.managementTotalCost;
  let managementCostThreshold = managementCostResultObj.managementCostThreshold;
  // if managementCostThreshold is a negative value, force it to zero
  managementCostThreshold = managementCostThreshold < 0 ? 0 : managementCostThreshold;

  // `Applicant management cost may not exceed ${maxPercentageOfManagementCost}% of the total application budget. Please see the maximum allowable applicant management cost for allowed applicant management cost.`
  return toResult(
    _.toNumber(managementTotalCost) <= _.toNumber(managementCostThreshold),
    null
  );
};

const passthroughMinimumThresholdRule = ({
  data,
  minPercentageOfPassthrough,
  fundingStreamSubType,
  fundingStreams,
  orgId,
  nsgpEmgpTribalflag = undefined,
}) => {
  const passthroughResultObj =
    fundingStreamSubType == 'HSGP'
      ? calculatePassthroughThresholdHSGP({data, minPercentageOfPassthrough})
      : calculatePassthroughThreshold(
          data,
          minPercentageOfPassthrough,
          fundingStreams,
          orgId,
          nsgpEmgpTribalflag
        );
  const sumTotalPassthrough = passthroughResultObj.sumTotalPassthrough;
  const minPassthroughThreshold = passthroughResultObj.minPassthroughThreshold;
  // `Passthrough costs must meet the minimum percentage set within the notice of funding opportunity.`
  return toResult(
    _.toNumber(sumTotalPassthrough) >= _.toNumber(minPassthroughThreshold),
    null
  );
};

const letpaMinimumThresholdRule = ({data, letpaMinPercentage}) => {
  let totalCost = _.reduce(
    _.get(data.budgetSummary, 'totalCharges', []),
    (acc, periodCharges) => safeAdd(acc, periodCharges),
    0
  );
  const letpaMinPercentDecimal = _.toNumber(letpaMinPercentage) / 100;
  let letpaMinThreshold = safeMult(
    safeSubstract(totalCost, _.toNumber(data.disciplineSummary.totalValues.OPSG)),
    letpaMinPercentDecimal
  );

  return toResult(
    safeAdd(
      _.toNumber(data.LETPASummary.groupByValues['Yes'].UASI) +
        _.toNumber(data.LETPASummary.groupByValues['Yes'].SHSP)
    ) >= _.toNumber(letpaMinThreshold),
    null
  );
};

const collectCostItemsForSubOrgs = (itemsWithOrgId, parentOrgId, nsgpEmgpTribalflag) => {
  const subOrgItems = [];
  _.forEach(itemsWithOrgId, (item) => {
    const orgId = _.get(item, PERFORMING_ORG_ID_PATH);
    const organizationType = _.get(
      item,
      'additionalInformation.organizationType',
      undefined
    );
    const memorandumOfUnderstandingInPlace = _.get(
      item,
      'additionalInformation.memorandumOfUnderstandingInPlace',
      undefined
    );

    if (
      (nsgpEmgpTribalflag && organizationType == LOCAL_AGENCY) ||
      (organizationType == STATE_AGENCY && memorandumOfUnderstandingInPlace)
    ) {
      subOrgItems.push(item);
    } else if (
      (!nsgpEmgpTribalflag || nsgpEmgpTribalflag == undefined) &&
      orgId &&
      orgId != parentOrgId
    ) {
      subOrgItems.push(item);
    }
  });

  let costItems = BudgetUtils.collectItemsContainingFieldPath([], COST, subOrgItems);
  return costItems;
};

const calculatePassthroughTotal = (fundingStreams, parentOrgId, nsgpEmgpTribalflag) => {
  const itemsWithOrgId = BudgetUtils.collectItemsContainingFieldPath(
    [],
    PERFORMING_ORG_ID_PATH,
    fundingStreams
  );
  const costItems = collectCostItemsForSubOrgs(
    itemsWithOrgId,
    parentOrgId,
    nsgpEmgpTribalflag
  );
  let passthroughTotal = 0;
  _.forEach(costItems, (costItem) => {
    const costs = _.get(costItem, COST, []);
    _.forEach(costs, (costAry) => {
      const total = _.get(costAry, '0.total');
      passthroughTotal += total;
    });
  });
  return passthroughTotal;
};

const calculatePassthroughThreshold = (
  data,
  minPercentageOfPassthrough,
  fundingStreams,
  orgId,
  nsgpEmgpTribalflag
) => {
  let total = _.reduce(
    _.get(data, 'budgetSummary.totalCharges', []),
    (acc, val) => (acc += val),
    0
  );
  const minThresholdPassthrough = safeMult(total, minPercentageOfPassthrough);
  const passthroughTotal = calculatePassthroughTotal(
    fundingStreams,
    orgId,
    nsgpEmgpTribalflag
  );

  return {
    sumTotalPassthrough: passthroughTotal,
    minPassthroughThreshold: minThresholdPassthrough,
  };
};

const calculatePassthroughThresholdHSGP = ({data, minPercentageOfPassthrough}) => {
  const totalPassthroughInvestmentsUASI = _.get(
    data.investmentBudgetSummary.subProgramTotals.totalPassthrough,
    UASI,
    0
  );
  const totalPassthroughInvestmentsSHSP = _.get(
    data.investmentBudgetSummary.subProgramTotals.totalPassthrough,
    SHSP,
    0
  );
  const totalInvestmentsSHSP = _.get(
    data.investmentBudgetSummary.subProgramTotals.totalInvestments,
    SHSP,
    0
  );
  const totalInvestmentsUASI = _.get(
    data.investmentBudgetSummary.subProgramTotals.totalInvestments,
    UASI,
    0
  );

  const totalManagementCostSHSPForOPSG = _.get(
    data.investmentBudgetSummary.subProgramTotals.totalManagementCost,
    SHSPFOROPSG,
    0
  );
  const totalManagementCostSHSPForSHSP = _.get(
    data.investmentBudgetSummary.subProgramTotals.totalManagementCost,
    SHSPFORSHSP,
    0
  );
  const totalManagementCostUASIForUASI = _.get(
    data.investmentBudgetSummary.subProgramTotals.totalManagementCost,
    UASIFORUASI,
    0
  );

  const totalPassthroughManagementCostSHSPForOPSG = _.get(
    data.investmentBudgetSummary.subProgramTotals.totalPassthroughManagementCost,
    SHSPFOROPSG,
    0
  );
  const totalPassthroughManagementCostSHSPForSHSP = _.get(
    data.investmentBudgetSummary.subProgramTotals.totalPassthroughManagementCost,
    SHSPFORSHSP,
    0
  );
  const totalPassthroughManagementCostUASIForUASI = _.get(
    data.investmentBudgetSummary.subProgramTotals.totalPassthroughManagementCost,
    UASIFORUASI,
    0
  );

  const sumTotalPassthrough = safeAdd(
    totalPassthroughInvestmentsUASI,
    totalPassthroughInvestmentsSHSP,
    totalPassthroughManagementCostSHSPForOPSG,
    totalPassthroughManagementCostSHSPForSHSP,
    totalPassthroughManagementCostUASIForUASI
  );
  const sumAllUASIandSHSP = safeAdd(
    totalInvestmentsUASI,
    totalInvestmentsSHSP,
    totalManagementCostSHSPForOPSG,
    totalManagementCostSHSPForSHSP,
    totalManagementCostUASIForUASI
  );
  const minPassthroughThreshold =
    _.toNumber(sumAllUASIandSHSP) * _.toNumber(minPercentageOfPassthrough);

  return {
    sumTotalPassthrough: sumTotalPassthrough,
    minPassthroughThreshold: minPassthroughThreshold,
  };
};

const managementCostOPSGForOPSGMaxThresholdRule = ({data, maxPercentage, message}) => {
  const totalInvestmentsOfSubProgram = _.get(
    data.investmentBudgetSummary.subProgramTotals.totalInvestments,
    OPSG,
    0
  );
  const totalManagementCostsOfSubProgram = _.get(
    data.investmentBudgetSummary.subProgramTotals.totalManagementCost,
    OPSGFOROPSG,
    0
  );

  const totalRecipientManagementCostsOfSubProgram = _.get(
    data.investmentBudgetSummary.subProgramTotals.totalRecipientManagementCost,
    OPSGFOROPSG,
    0
  );

  const totalManagementCostsOfSubProgramWithRecipientCosts = safeAdd(
    totalRecipientManagementCostsOfSubProgram,
    totalManagementCostsOfSubProgram
  );

  const passthroughResultObj = calculateManagementCostSubProgramThreshold({
    maxPercentage,
    totalInvestmentsOfSubProgram,
    totalManagementCostsOfSubProgram: totalManagementCostsOfSubProgramWithRecipientCosts,
  });
  const maxThreshold = passthroughResultObj.maxThreshold;
  return {
    isValid:
      _.toNumber(totalManagementCostsOfSubProgramWithRecipientCosts) <=
      _.toNumber(maxThreshold),
    message: message,
    path: 'costTypeError',
    customText: ``,
  };
};

const managementCostSHSPForOPSGMaxThresholdRule = ({data, maxPercentage, message}) => {
  const totalInvestmentsOfSubProgram = _.get(
    data.investmentBudgetSummary.subProgramTotals.totalInvestments,
    OPSG,
    0
  );
  const totalManagementCostsOfSubProgram = _.get(
    data.investmentBudgetSummary.subProgramTotals.totalManagementCost,
    OPSGFOROPSG,
    0
  );
  const totalManagementCostsOfSubProgramRecipient = _.get(
    data.investmentBudgetSummary.subProgramTotals.totalRecipientManagementCost,
    OPSGFOROPSG,
    0
  );

  const totalManagementCostsOfSubProgramWithRecipientCosts = safeAdd(
    totalManagementCostsOfSubProgramRecipient,
    totalManagementCostsOfSubProgram
  );

  const totalManagementCostSHSPForOPSG = _.get(
    data.investmentBudgetSummary.subProgramTotals.totalManagementCost,
    SHSPFOROPSG,
    0
  );

  const totalManagementCostSHSPForOPSGRecipient = _.get(
    data.investmentBudgetSummary.subProgramTotals.totalRecipientManagementCost,
    SHSPFOROPSG,
    0
  );

  const totalManagementCostSHSPForOPSGWithRecipientCosts = safeAdd(
    totalManagementCostSHSPForOPSGRecipient,
    totalManagementCostSHSPForOPSG
  );

  const passthroughResultObj = calculateManagementCostSubProgramThreshold({
    maxPercentage,
    totalInvestmentsOfSubProgram,
    totalManagementCostsOfSubProgram: totalManagementCostsOfSubProgramWithRecipientCosts,
  });
  const maxThreshold = passthroughResultObj.maxThreshold;
  return {
    isValid:
      _.toNumber(totalManagementCostSHSPForOPSGWithRecipientCosts) <=
      _.toNumber(maxThreshold),
    message: message,
    path: 'costTypeError',
    customText: ``,
  };
};

const managementCostSHSPForSHSPMaxThresholdRule = ({data, maxPercentage, message}) => {
  const totalInvestmentsOfSubProgram = _.get(
    data.investmentBudgetSummary.subProgramTotals.totalInvestments,
    SHSP,
    0
  );
  const totalManagementCostSHSPForSHSP = _.get(
    data.investmentBudgetSummary.subProgramTotals.totalManagementCost,
    SHSPFORSHSP,
    0
  );

  const totalManagementCostSHSPForSHSPRecipient = _.get(
    data.investmentBudgetSummary.subProgramTotals.totalRecipientManagementCost,
    SHSPFORSHSP,
    0
  );
  const totalManagementCostsOfSubProgramWithRecipient = safeAdd(
    totalManagementCostSHSPForSHSP,
    totalManagementCostSHSPForSHSPRecipient
  );

  const totalManagementCostSHSPForOPSG = _.get(
    data.investmentBudgetSummary.subProgramTotals.totalManagementCost,
    SHSPFOROPSG,
    0
  );
  const totalManagementCostSHSPForOPSGRecipient = _.get(
    data.investmentBudgetSummary.subProgramTotals.totalRecipientManagementCost,
    SHSPFOROPSG,
    0
  );
  const totalManagementCostsOfSubProgram = safeAdd(
    totalManagementCostSHSPForSHSP,
    totalManagementCostSHSPForOPSG,
    totalManagementCostSHSPForSHSPRecipient,
    totalManagementCostSHSPForOPSGRecipient
  );

  const passthroughResultObj = calculateManagementCostSubProgramThreshold({
    maxPercentage,
    totalInvestmentsOfSubProgram,
    totalManagementCostsOfSubProgram,
  });
  const maxThreshold = passthroughResultObj.maxThreshold;
  return {
    isValid:
      _.toNumber(totalManagementCostsOfSubProgramWithRecipient) <=
      _.toNumber(maxThreshold),
    message: message,
    path: 'costTypeError',
    customText: ``,
  };
};

const managementCostUASIForUASIMaxThresholdRule = ({data, maxPercentage, message}) => {
  const totalInvestmentsOfSubProgram = _.get(
    data.investmentBudgetSummary.subProgramTotals.totalInvestments,
    UASI,
    0
  );
  const totalManagementCostsOfSubProgram = _.get(
    data.investmentBudgetSummary.subProgramTotals.totalManagementCost,
    UASIFORUASI,
    0
  );
  const totalManagementCostsOfSubProgramRecipient = _.get(
    data.investmentBudgetSummary.subProgramTotals.totalRecipientManagementCost,
    UASIFORUASI,
    0
  );

  const totalManagementCostsOfSubProgramWithRecipient = safeAdd(
    totalManagementCostsOfSubProgram,
    totalManagementCostsOfSubProgramRecipient
  );

  const passthroughResultObj = calculateManagementCostSubProgramThreshold({
    maxPercentage,
    totalInvestmentsOfSubProgram,
    totalManagementCostsOfSubProgram: totalManagementCostsOfSubProgramWithRecipient,
  });
  const maxThreshold = passthroughResultObj.maxThreshold;
  return {
    isValid:
      _.toNumber(totalManagementCostsOfSubProgramWithRecipient) <=
      _.toNumber(maxThreshold),
    message: message,
    path: 'costTypeError',
    customText: ``,
  };
};

const calculateManagementCostSubProgramThreshold = ({
  maxPercentage,
  totalInvestmentsOfSubProgram,
  totalManagementCostsOfSubProgram,
}) => {
  const sumTotal = safeAdd(
    totalInvestmentsOfSubProgram,
    totalManagementCostsOfSubProgram
  );
  const maxThreshold = _.toNumber(sumTotal) * _.toNumber(maxPercentage) * 0.01;

  return {
    sumTotal: sumTotal,
    maxThreshold: maxThreshold,
  };
};

const managementCostSectionMaxThresholdRuleSubRecipient = ({
  data,
  config,
  message,
  appOrgId,
  orgIdPerformingWork,
  maxPercentageOfSubRecipientManagementCost,
}) => {
  const fundingStreams = config.fundingStreams;
  let totalSubRecipientCosts = 0;
  let matchingFundingStream = _.filter(fundingStreams, (stream) => {
    return _.includes(INVESTMENT, stream.fundingStreamType);
  });

  let totalManagementCostSubRecipient = sumTotalSubRecipientManagementCosts(
    matchingFundingStream,
    appOrgId,
    false,
    orgIdPerformingWork
  );
  if (_.get(data, 'subrecipientSumary.groupByValues')[orgIdPerformingWork]) {
    totalSubRecipientCosts = _.get(data, 'subrecipientSumary.groupByValues')[
      orgIdPerformingWork
    ].total;
  }

  let managementCostSubRecipientThreshold =
    _.toNumber(totalSubRecipientCosts) *
    _.toNumber(maxPercentageOfSubRecipientManagementCost) *
    0.01;

  return {
    isValid:
      _.toNumber(totalManagementCostSubRecipient) <=
      _.toNumber(managementCostSubRecipientThreshold),
    message: message,
    path: 'costTypeError',
    customText: `(Maximum allowable cost: ${showDollarAmountWithDollar(
      roundToNearestPenny(managementCostSubRecipientThreshold)
    )})`,
  };
};

const managementCostSectionMaxThresholdRule = ({data, config, costshareFS, message}) => {
  const fundingStreams = config.fundingStreams;
  const managementCostResultObj = calculateManagementCostThreshold({
    data,
    fundingStreams,
    costshareFS,
  });
  const managementTotalCost = managementCostResultObj.managementTotalCost;
  const managementCostThreshold = managementCostResultObj.managementCostThreshold;
  return {
    isValid: _.toNumber(managementTotalCost) <= _.toNumber(managementCostThreshold),
    message: message,
    path: 'costTypeError',
    customText: `(Maximum allowable cost: ${showDollarAmountWithDollar(
      roundToNearestPenny(managementCostThreshold)
    )})`,
  };
};

const validateFedShareValuesWithCommitment = ({
  message,
  amendmentFedShare,
  commitment,
  grantFedShare,
  commitmentAvailableAmountDataPath,
}) => {
  const fedShareAmendment = _.isArray(amendmentFedShare)
    ? _.ceil(_.sum(_.map(amendmentFedShare, _.toNumber)))
    : amendmentFedShare;
  const fedShareGrant = _.isArray(grantFedShare)
    ? _.ceil(_.sum(_.map(grantFedShare, _.toNumber)))
    : grantFedShare;
  const commitmentAvailableAmount = _.get(
    commitment,
    commitmentAvailableAmountDataPath
      ? commitmentAvailableAmountDataPath
      : 'commitmentDetailsResponse[0].commitmentAvailableAmount'
  );
  const fedShare = _.subtract(fedShareAmendment, fedShareGrant);
  const comparison = {
    allowMore: false,
    allowLess: true,
    allowEqual: true,
  };
  return isCompareValue({
    comparison,
    value: commitmentAvailableAmount,
    message,
    data: fedShare,
  });
};

const validateFedShareValuesWithPayments = ({message, amendmentFedShare, payments}) => {
  const fedShare = Array.isArray(amendmentFedShare)
    ? _.sum(_.map(amendmentFedShare, _.toNumber))
    : amendmentFedShare;
  const totalDisbursed = calculatePaymentDispursements(payments).federalDispursements;
  const comparison = {
    allowMore: true,
    allowLess: false,
    allowEqual: true,
  };
  return isCompareValue({comparison, value: totalDisbursed, message, data: fedShare});
};

const sumFundingHolds = (terms) => {
  let sum = 0;
  terms.forEach((term) => {
    if (term.financialHoldAmount) sum = safeAdd(sum, term.financialHoldAmount);
  });
  return sum;
};

const validatePaymentWithAvailableResources = ({
  requestedAmount,
  message,
  grantFedShare,
  payments,
  terms,
}) => {
  const fedShare = Array.isArray(grantFedShare)
    ? _.sum(_.map(grantFedShare, _.toNumber))
    : grantFedShare;
  const totalHolds = sumFundingHolds(terms);
  const totalDisbursed = calculatePaymentDispursements(payments).federalDispursements;
  const comparison = {
    allowMore: true,
    allowLess: false,
    allowEqual: true,
  };
  const totalLess = safeAdd(totalHolds, totalDisbursed);
  return isCompareValue({
    comparison,
    value: requestedAmount,
    message,
    data: safeSubstract(fedShare, totalLess),
  });
};

const validateFedShareValuesWithPaymentsForSubgrants = ({
  message,
  amendmentFedShare,
  payments,
  subGrantId,
}) => {
  const fedShare = Array.isArray(amendmentFedShare)
    ? _.sum(_.map(amendmentFedShare, _.toNumber))
    : amendmentFedShare;
  const totalDisbursed = calculatePaymentDispursementsForSubGrants(payments, subGrantId)
    .federalDispursements;
  const comparison = {
    allowMore: true,
    allowLess: false,
    allowEqual: true,
  };
  return isCompareValue({comparison, value: totalDisbursed, message, data: fedShare});
};

const compareCostItemTotalValue = ({costs, comparison, value, message}) => {
  const totalCosts = getCostsTotalForAttribute(costs, TOTAL);
  return isCompareValue({comparison, value, message, data: totalCosts});
};

const compareGrandTotalValue = ({
  fundingStreams,
  mode,
  comparison,
  value,
  message,
  fundingStreamsToCompareTo,
}) => {
  const total = calculateTotalFundingStreams({fundingStreams, mode});
  //adding this to be able to compare two funding stream values instead of a stated value. will overwrite the valueif provided.
  if (fundingStreamsToCompareTo) {
    value = calculateTotalFundingStreams({
      fundingStreams: fundingStreamsToCompareTo,
      mode,
    });
  }
  return isCompareValue({comparison, value, message, data: total});
};

const doesNotContainZeroAnnualCost = ({fundingStreams, message}) => {
  let hasZeroAnnualCost = false;
  var costArrays = JSONPath.query(fundingStreams, '$..cost');
  _.forEach(costArrays, (costArray) => {
    _.map(costArray, (costs) => {
      let annualCost = _.toNumber(costs[0].price) + _.toNumber(costs[1].price);
      if (annualCost === 0) hasZeroAnnualCost = true;
    });
  });
  return toResult(!hasZeroAnnualCost, message);
};

const arrSumGreaterThan = ({data, message, min}) => {
  return toResult(_.toNumber(data.reduce((a, b) => a + b, 0)) > _.toNumber(min), message);
};

const arrSumLessThanOrEqualTo = ({data, message, max}) => {
  return toResult(
    _.toNumber(data.reduce((a, b) => a + b, 0)) <= _.toNumber(max),
    message
  );
};

const arrAttrSumEqualTo = ({data, message, value, attributes}) => {
  let sum = arrAttrSum({
    array: data,
    attributes: attributes,
  });
  return toResult(_.toNumber(sum) === _.toNumber(value), message);
};

const arrAttrRoundSumEqualTo = ({data, message, value, attributes}) => {
  let sum = arrAttrSum({
    array: data,
    attributes: attributes,
  });
  const roundSum = _.round(sum, 2);
  return toResult(_.toNumber(roundSum) === _.toNumber(value), message);
};

const arrAttrSumLessThanOrEqualTo = ({data, message, max, attributes}) => {
  return toResult(
    arrAttrSum({
      array: data,
      attributes: attributes,
    }) <= max,
    message
  );
};

const arrAttrSumGreaterThan = ({data, message, min, attributes}) => {
  return toResult(
    arrAttrSum({
      array: data,
      attributes: attributes,
    }) > min,
    message
  );
};

// runs validations for each field in object (less flexible than validateObject, but more simplified syntax)
// does not allow arbitrary arguments and dependencies
const validateNestedRules = ({data, fieldValidations}) => {
  let resultError = {};
  let allValid = true;
  _.forEach(fieldValidations, (validation, field) => {
    if (validation.rules) {
      _.forEach(validation.rules, (validationRule, index) => {
        const ruleResult = ruleSet[validationRule.rule]({
          ...validationRule,
          data: validationRule.rule === 'nestedRunRuleIf' ? data : _.get(data, field),
        });

        const result = {
          isValid: ruleResult.isValid,
          message:
            validationRule.rule === 'nestedRunRuleIf'
              ? ruleResult.message
              : validationRule.message,
        };
        _.set(resultError, `${field}[${index}]`, result);
        allValid = allValid && result.isValid;
      });
    } else {
      const ruleResult = ruleSet[validation.rule]({
        data: validation.rule === 'nestedRunRuleIf' ? data : _.get(data, field),
      });

      const result = {
        isValid: ruleResult.isValid,
        message:
          validation.rule === 'nestedRunRuleIf' ? ruleResult.message : validation.message,
      };
      _.set(resultError, field, result);
      allValid = allValid && result.isValid;
    }
  });
  resultError.isValid = allValid;
  return resultError;
};

//Takes in an array and runs validations for each field in every element (less flexible than validateObjectForArray, but more simplified syntax)
// does not allow arbitrary arguments and dependencies
const validateNestedRulesForArray = ({data, fieldValidations}) => {
  let resultError = {};
  let allValid = true;
  _.forEach(data, (obj, index) => {
    const result = validateNestedRules({data: obj, fieldValidations});
    _.set(resultError, index, result);
    allValid = allValid && result.isValid;
  });
  resultError.isValid = allValid;
  return resultError;
};

const validateNonFedShareValues = ({data, nonFedShare}) => {
  let resultError = {};
  let totalNonFedAmount = 0;
  if (nonFedShare !== undefined) {
    let nonFedShareAmt = parseFloat(_.sum(_.map(nonFedShare, _.toNumber)).toFixed(2));

    _.forEach(data, (obj, index) => {
      totalNonFedAmount += _.toNumber(obj.amount);
    });
    totalNonFedAmount = parseFloat(totalNonFedAmount.toFixed(2));
    if (totalNonFedAmount !== nonFedShareAmt) {
      return toResult(false, 'Non Fedshare amount should be equal to Funding Source');
    } else {
      return toResult(true, 'Non Fedshare amount should be equal to Funding Source');
    }
  }
  return resultError;
};

//Takes in an array and runs validations for each field in every element (more flexible than validateNestedRulesForArray, but less simplified syntax)
// allows arbitrary arguments and dependencies
const validateObjectForArray = ({
  data,
  fieldValidations,
  validationContext,
  dataPath,
  quantity,
  message,
}) => {
  let resultError = {};
  let allValid = true;
  let someValid = false;
  let method = quantity === 'some' ? 'some' : 'all';
  _.forEach(data, (obj, index) => {
    let validationContextWithFieldPath = {
      ...validationContext,
      root: obj,
      index: index,
    };
    const result = validateObject({
      fieldValidations,
      validationContext: validationContextWithFieldPath,
      dataPath: dataPath,
    });
    _.set(resultError, [ERROR, index], result);
    allValid = allValid && result.isValid;
    someValid = someValid || result.isValid;
  });
  resultError.isValid = method === 'some' ? someValid : allValid;
  if (message && !resultError.isValid) {
    resultError.message = message;
  }
  return resultError;
};

const validateObjectForArrayWithinArray = ({
  data,
  fieldValidations,
  validationContext,
  dataPath,
}) => {
  let resultError = {};
  let allValid = true;
  _.forEach(data, (obj, index) => {
    let validationContextWithFieldPath = {
      ...validationContext,
      subRoot: obj,
      index: index,
    };
    const result = validateObject({
      fieldValidations,
      validationContext: validationContextWithFieldPath,
      dataPath: dataPath,
    });
    _.set(resultError, [ERROR, index], result);
    allValid = allValid && result.isValid;
  });
  resultError.isValid = allValid;
  return resultError;
};

const isNotActiveTask = (input) => {
  const {taskName, data} = input;
  let isValid = true;
  if (
    _.get(
      _.find(data, (task) => task.name === taskName),
      'name'
    )
  ) {
    isValid = false;
  }
  return toResult(isValid, input.message);
};

//DO NOT USE need to remove in Cleanup consider runRulesRequestDetails
const requestDetailsPreAwardV2 = ({data: fundingStreams, allItems}) => {
  let result = {};
  let allValid = true;
  let errorsObj = {};
  const findCostType = (itemCode) =>
    _.get(
      _.find(allItems, (item) => item.itemCode === itemCode),
      'costType'
    );
  const fieldValidations = {
    staffingBundled: ['additionalInformation.description', 'itemCode'],
    simple: ['additionalInformation.description', 'itemCode'],
  };
  const itemOfCostFields = ['boc', 'price', 'quantity'];
  const addError = (path, value) => {
    const isValid = !checkIfValuesAre([null, undefined, '', {}, []], value);
    allValid = allValid && isValid;
    _.setWith(errorsObj, path, {isValid}, Object);
    return isValid;
  };

  const findCostErrors = (costs, path) => {
    let isValid = true;
    _.forEach(costs, (allCosts, allCostsIndex) =>
      _.forEach(allCosts, (cost, index) =>
        _.forEach(itemOfCostFields, (field) => {
          isValid =
            isValid &&
            addError(
              `${path}.cost.[${allCostsIndex}].[${index}].${field}`,
              _.get(cost, field)
            );
        })
      )
    );
    return isValid;
  };

  const requiresJustification = (node) => {
    const hasChanges = !_.isEmpty(_.get(node, 'additionalInformation.changes'));
    const hasItemsOfCost = !_.isEmpty(_.get(node, 'cost'));

    return hasChanges && hasItemsOfCost;
  };

  const findErrors = (children, currPath) => {
    _.forEach(children, (child, childKey) => {
      let isValid = true;
      let path = `${currPath}.${childKey}`;
      const costType = findCostType(child.itemCode);
      if (_.isEqual(child.itemCode, 8)) {
        isValid =
          isValid &&
          addError(
            `${path}.additionalInformation.explainOtherItem`,
            _.get(child, 'additionalInformation.explainOtherItem')
          );
      }
      if (costType) {
        const fieldValidationsToRun = fieldValidations[costType];
        _.forEach(
          fieldValidationsToRun,
          (field) =>
            (isValid = isValid && addError(`${path}.${field}`, _.get(child, field)))
        );
      } else {
        isValid = isValid && addError(`${path}.missingChildren`, child.children);
      }
      if (requiresJustification(child)) {
        isValid =
          isValid &&
          addError(
            `${path}.additionalInformation.justification`,
            _.get(child, 'additionalInformation.justification')
          );
      }
      isValid = isValid && findCostErrors(child.cost, path);
      _.set(errorsObj, `${path}.isValid`, isValid);
      findErrors(child.children, `${path}.children`);
    });
  };

  _.forEach(fundingStreams, (fundingStream, fundingIndex) =>
    findErrors(fundingStream.children, `[${fundingIndex}].children`)
  );
  result.results = errorsObj;
  result.isValid = allValid;
  result.message = allValid ? null : 'Required fields missing.';
  return result;
};

const awardAcceptanceWithFields = ({
  data: awardAcceptance,
  extraFields,
  determinationMapping,
  comments,
  determination,
}) => {
  extraFields = extraFields || {};

  const checkIfValid = (acc, field) => {
    return (
      acc &&
      isRequired({data: _.get(awardAcceptance, field)}).isValid &&
      _.get(awardAcceptance, field) != false
    ); // just checkboxes for now
  };

  if (determination == determinationMapping.accepted) {
    const checkExtraFields = _.reduce(extraFields.accepted, checkIfValid, true);
    return toResult(checkExtraFields, 'Missing required fields');
  } else if (determination === determinationMapping.declined) {
    const checkExtraFields = _.reduce(extraFields.declined, checkIfValid, true);
    return toResult(
      !_.isEmpty(_.trim(comments.declined)) && checkExtraFields,
      'Missing required fields'
    );
  } else {
    return toResult(false, 'Missing determination');
  }
};

const bcaValidateDocuments = (props) => {
  let isValid = true;

  if (props.data === props.expectedValue) {
    isValid = _.size(_.get(props, 'attachmentIds')) >= props.noOfMinAttachments;
  }

  return toResult(isValid, props.message);
};

// test attributes of objects in a list ("all have <x>" or "any have <y>" or "none have <z>" etc)
const objectListAttributes = ({
  data,
  quantity,
  positiveCheck,
  attribute,
  toMatch,
  uniqueness,
  message,
}) => {
  const TYPES = ['some', 'all'];
  if (
    attribute === undefined ||
    (uniqueness === undefined &&
      (!_.includes(TYPES, quantity) ||
        positiveCheck === undefined ||
        toMatch === undefined))
  ) {
    return toResult(false, 'Invalidly configured rule.');
  }

  if (uniqueness) {
    const firstAttrValue = _.get(data, [0, attribute]);
    const values = uniqueness === 'same' ? {[firstAttrValue]: true} : {};
    let isValid = true;
    _.forEach(data, (item) => {
      const attributeValue = _.get(item, attribute);
      if (
        (uniqueness === 'different' && values[attributeValue] !== undefined) ||
        (uniqueness === 'same' && values[attributeValue] === undefined)
      ) {
        isValid = false;
      }
      values[attributeValue] = true;
    });
    return toResult(isValid, message);
  } else {
    const numberOfMatches = _.size(
      _.filter(data, (item) => {
        const has = _.includes(toMatch, _.get(item, attribute));
        return positiveCheck ? has : !has;
      })
    );

    return toResult(
      quantity == 'some' ? numberOfMatches > 0 : numberOfMatches === _.size(data),
      message
    );
  }
};

const validateNoActiveEmployee = ({data, roster, message}) => {
  const rosterId = _.get(data, 'rosterId');
  const postionId = _.get(data, 'positionId');
  const position = getPosition(postionId, roster);

  const isValid = isExistingPersonnel(rosterId, roster)
    ? true
    : !containsActivePersonnel(position);
  return toResult(isValid, message);
};

const containsActivePersonnel = (position) => {
  const employees = _.get(position, 'employees', null);
  const index = _.findIndex(employees, (employee) => {
    return _.isEmpty(_.get(employee, 'endDate'));
  });

  return employees ? index >= 0 : false;
};

const validatePersonnelStartDate = ({data, roster, message}) => {
  const startDate = moment(_.get(data, 'startDate'));
  const currentEndDate = _.get(data, 'endDate');
  const endDate = currentEndDate ? moment(currentEndDate) : undefined;
  const positionId = _.get(data, 'positionId');
  const rosterId = _.get(data, 'rosterId');
  const position = getPosition(positionId, roster);

  const isValid = !startDateOverlapsOtherEmployee({
    rosterId,
    position,
    startDate,
    endDate,
  });
  return toResult(isValid, message);
};

const validateActivePersonnelStartDate = ({data, roster, message}) => {
  // For active employee, validate that start date is after all end dates of inactive employees
  const notFunded = _.get(data, 'notFunded');
  if (notFunded) return toResult(true, message);

  const startDate = moment(_.get(data, 'startDate'));
  const positionId = _.get(data, 'positionId');
  const rosterId = _.get(data, 'rosterId');
  const position = getPosition(positionId, roster);

  let isValid = true;
  if (!isStartDateAfterAllEndDates({rosterId, position, startDate})) {
    isValid = false;
  } else {
    isValid = true;
  }
  return toResult(isValid, message);
};

const isStartDateAfterAllEndDates = ({rosterId, position, startDate}) => {
  const employees = _.get(position, 'employees');
  const index = _.findIndex(employees, (employee) => {
    const emplEndDate = _.get(employee, 'endDate');
    let value =
      !emplEndDate || employee.rosterId === rosterId
        ? false
        : startDate.isBefore(moment(emplEndDate), 'day');

    return value;
  });

  return index < 0;
};

const validatePersonnelEndDate = ({data, roster, message}) => {
  const dataEndDate = _.get(data, 'endDate');
  const endDate = dataEndDate ? moment(dataEndDate) : null;
  const dataStartDate = _.get(data, 'startDate');

  const startDate = dataStartDate ? moment(dataStartDate) : null;
  // This should never happen
  if (!startDate) return toResult(false, 'Start date for employment must be specified.');

  const positionId = _.get(data, 'positionId');
  const rosterId = _.get(data, 'rosterId');
  const position = getPosition(positionId, roster);

  let isValid = true;

  if (dataEndDate) {
    // Check for existing employee only if more than one employee in position
    isValid =
      _.size(_.get(position, 'employees', [])) === 1 &&
      isExistingPersonnel(rosterId, roster)
        ? true
        : !endDateOverlapsOtherEmployee({rosterId, position, startDate, endDate});
  }

  return toResult(isValid, message);
};

const startDateOverlapsOtherEmployee = ({rosterId, position, startDate, endDate}) => {
  // return true of startDate is on or before the endDate of any employees that worked PRIOR to
  // the workdate range being tested (startDate - endDate)
  let overlap = false;
  const employees = _.get(position, 'employees');
  let index = _.findIndex(employees, (employee) => {
    if (employee.rosterId != rosterId) {
      const emplStart = _.get(employee, 'startDate');
      const emplEnd = _.get(employee, 'endDate');
      const emplStartDate = emplStart ? moment(emplStart) : null;
      const emplEndDate = emplEnd ? moment(emplEnd) : null;
      if (endDate && emplStartDate && endDate.isAfter(emplStartDate, 'day')) {
        if (emplEndDate && startDate.isSameOrBefore(emplEndDate, 'day')) {
          overlap = true;
        }
      }
    }
    return overlap;
  });
  return index >= 0;
};

const endDateOverlapsOtherEmployee = ({rosterId, position, startDate, endDate}) => {
  // return true of endDate is on or after the startDate of any employees that worked AFTER
  // the workdate range being tested (startDate - endDate)
  let overlap = false;
  const employees = _.get(position, 'employees');
  let index = _.findIndex(employees, (employee) => {
    if (employee.rosterId != rosterId) {
      const emplStart = _.get(employee, 'startDate');
      const emplEnd = _.get(employee, 'endDate');
      const emplStartDate = emplStart ? moment(emplStart) : null;
      const emplEndDate = emplEnd ? moment(emplEnd) : null;
      const notFunded = _.get(employee, 'notFunded'); // if false, employee is ACTIVE and has no emplEndDate
      if (!notFunded || (emplEndDate && startDate.isBefore(emplEndDate, 'day'))) {
        if (emplStartDate && endDate.isSameOrAfter(emplStartDate, 'day')) {
          overlap = true;
        }
      }
    }
    return overlap;
  });
  return index >= 0;
};

const getPosition = (positionId, roster) => {
  const position = _.isEmpty(positionId)
    ? null
    : _.find(roster, (pos) => {
        if (_.isArray(pos.employees) && !_.isEmpty(pos.employees)) {
          return pos.employees[0].positionId == positionId;
        }
      });
  return position;
};

const isExistingPersonnel = (rosterId, roster) => {
  const existingEmployee = _.find(
    _.flatMap(_.map(roster, (pos) => _.get(pos, 'employees', []))),
    (employee) => employee.rosterId === rosterId
  );
  return existingEmployee ? true : false;
};

const validateDate = ({
  data,
  allowCurrentDay,
  allowPast,
  allowFuture,
  checkForTime,
  dateAllowedEarliest,
  dateAllowedEarliestOnlyDate,
  dateAllowedLatest,
  dateAllowedAfter,
  dateAllowedSameOrAfter,
  dateAllowedAfterWithTime,
  dateAllowedBefore,
  dateAllowedBeforeWithTime,
  dateFormat,
  messageDateFormat,
  noMoreThanDaysInFuture,
  checkForDateTime,
  message,
}) => {
  let m = moment;
  if (!data) {
    return toResult(true, 'There is no date to validate.');
  }
  if (!moment(data, dateFormat || 'MM/DD/YYYY', true).isValid()) {
    return toResult(false, 'You must enter a valid date.');
  }

  if (dateFormat == 'MM/YYYY') {
    data = data.slice(0, 2) + '/01/' + data.slice(3, 7);
    if (dateAllowedEarliest) {
      dateAllowedEarliest =
        dateAllowedEarliest.slice(0, 2) + '/01/' + dateAllowedEarliest.slice(3, 7);
    }
  }

  const now = moment();
  const date = moment(data);

  if (checkForDateTime && dateAllowedEarliestOnlyDate) {
    if (
      !moment(dateAllowedEarliestOnlyDate, dateFormat || 'MM/DD/YYYY', true).isValid()
    ) {
      return toResult(false, 'You must enter a valid date.');
    }
    var isBefore = moment(data).utc().isBefore(moment(dateAllowedEarliestOnlyDate).utc());
    if (isBefore) {
      return toResult(
        false,
        message ||
          `Date must be no earlier than ${moment(dateAllowedEarliestOnlyDate)
            .utc()
            .format('MM/DD/YYYY HH:mm')}`
      );
    } else {
      return toResult(true, 'Is valid date');
    }
  }

  if (!allowCurrentDay && date.isSame(now, 'day')) {
    return toResult(false, message || 'Date must not be today');
  }
  if (!allowFuture && date.isAfter(now, 'day')) {
    return toResult(false, message || 'Date must not be in the future.');
  }
  if (!allowPast && ((checkForTime && date.isBefore(now)) || date.isBefore(now, 'day'))) {
    return toResult(false, message || 'Date must not be in the past');
  }
  if (dateAllowedEarliest && date.isBefore(moment(dateAllowedEarliest).utc())) {
    return toResult(
      false,
      message ||
        `Date must be no earlier than ${moment(dateAllowedEarliest)
          .utc()
          .format(dateFormat || 'MM/DD/YYYY')}`
    );
  }
  if (dateAllowedAfterWithTime && !date.isAfter(moment(dateAllowedAfterWithTime).utc())) {
    return toResult(
      false,
      message ||
        `Date must be later than ${moment(dateAllowedAfterWithTime)
          .utc()
          .format(messageDateFormat || 'MM/DD/YYYY')}`
    );
  }
  if (
    dateAllowedBeforeWithTime &&
    !date.isBefore(moment(dateAllowedBeforeWithTime).utc())
  ) {
    return toResult(
      false,
      message ||
        `Date must be earlier than ${moment(dateAllowedBeforeWithTime)
          .utc()
          .format(messageDateFormat || 'MM/DD/YYYY')}`
    );
  }
  // Compare dates only without time or timezone
  if (
    dateAllowedEarliestOnlyDate &&
    date.isBefore(moment(dateAllowedEarliestOnlyDate).utc().format('MM/DD/YYYY'))
  ) {
    return toResult(
      false,
      message ||
        `Date must be no earlier than ${moment(dateAllowedEarliestOnlyDate)
          .utc()
          .format('MM/DD/YYYY')}`
    );
  }
  if (
    dateAllowedLatest &&
    date.isAfter(moment(dateAllowedLatest).utc().format('MM/DD/YYYY'))
  ) {
    return toResult(
      false,
      message ||
        `Date must be no later than ${moment(dateAllowedLatest)
          .utc()
          .format('MM/DD/YYYY')}`
    );
  }
  if (dateAllowedAfter && !date.isAfter(moment(dateAllowedAfter).utc())) {
    return toResult(
      false,
      message ||
        `Date must be later than ${moment(dateAllowedAfter).utc().format('MM/DD/YYYY')}`
    );
  }
  if (
    dateAllowedSameOrAfter &&
    !date.isSameOrAfter(moment(dateAllowedSameOrAfter).utc())
  ) {
    return toResult(
      false,
      message ||
        `Date must be later than ${moment(dateAllowedSameOrAfter)
          .utc()
          .format('MM/DD/YYYY')}`
    );
  }
  if (dateAllowedBefore && !date.isBefore(moment(dateAllowedBefore).utc())) {
    return toResult(
      false,
      message ||
        `Date must be earlier than ${moment(dateAllowedBefore)
          .utc()
          .format('MM/DD/YYYY')}`
    );
  }

  if (
    noMoreThanDaysInFuture &&
    date.isAfter(now.add(noMoreThanDaysInFuture, 'days').utc())
  ) {
    return toResult(
      false,
      message ||
        `Date must be later than ${now
          .add(noMoreThanDaysInFuture, 'days')
          .utc()
          .format('MM/DD/YYYY')}`
    );
  }
  return toResult(true, 'Is valid date');
};

const validatePOPEndDate = ({
  data,
  allowCurrentDay,
  allowPast,
  allowFuture,
  checkForTime,
  dateAllowedEarliest,
  dateAllowedEarliestOnlyDate,
  dateAllowedLatest,
  dateAllowedAfter,
  fundingOpportunityCommitments,
  selectedCommitments,
  dateFormat,
  noMoreThanDaysInFuture,
  message,
}) => {
  let dateAllowedBefore = '';

  if (selectedCommitments) {
    _.forEach(selectedCommitments, (selectedCommitment) => {
      //for each selectedCommitment find corresponding funding opportunity commitment and get earliest popAmendmentCutoffDate
      const commitment = _.find(fundingOpportunityCommitments, [
        'commitmentNumbers',
        selectedCommitment.commitmentNumberId,
      ]);
      if (commitment && commitment.popAmendmentCutoffDate) {
        if (
          _.isEmpty(dateAllowedBefore) ||
          moment(commitment.popAmendmentCutoffDate).isBefore(
            moment(dateAllowedBefore).utc()
          )
        ) {
          dateAllowedBefore = commitment.popAmendmentCutoffDate;
        }
      }
    });
  }

  return validateDate({
    data,
    allowCurrentDay,
    allowPast,
    allowFuture,
    checkForTime,
    dateAllowedEarliest,
    dateAllowedEarliestOnlyDate,
    dateAllowedLatest,
    dateAllowedAfter,
    dateAllowedBefore,
    dateFormat,
    noMoreThanDaysInFuture,
    message,
  });
};

const validateDateForPOP = ({
  data,
  paymentStartDate,
  pop,
  dateAllowedEarliest,
  dateFormat,
  message,
}) => {
  if (!data) {
    return toResult(true, 'There is no date to validate.');
  }
  if (!moment(data, dateFormat || 'MM/DD/YYYY', true).isValid()) {
    return toResult(false, 'You must enter a valid date.');
  }

  const date = moment(data);
  let currentFundingPeriod = {};
  const startDate = moment(_.get(pop, 'startDate'));
  const lastPaymentStartDate = moment(paymentStartDate);
  if (!_.isEmpty(pop)) {
    // Rounding because end date is not included
    const years = Math.round(
      moment(_.get(pop, 'endDate')).diff(startDate, 'years', true)
    );
    // Finding current POP
    for (let i = 0; i < years; i++) {
      const periodStartDate = moment(startDate).add(i, 'years');
      const periodEndDate = moment(startDate)
        .add(i + 1, 'years')
        .subtract(1, 'days');

      if (
        (lastPaymentStartDate.isBetween(periodStartDate, periodEndDate) &&
          !moment(dateAllowedEarliest).isBetween(periodStartDate, periodEndDate)) ||
        (lastPaymentStartDate.isBetween(periodStartDate, periodEndDate) &&
          moment(dateAllowedEarliest).isBetween(periodStartDate, periodEndDate))
      ) {
        currentFundingPeriod.start = periodStartDate;
        currentFundingPeriod.end = periodEndDate;
      }
    }
    if (
      !_.isEmpty(currentFundingPeriod) &&
      date.isAfter(currentFundingPeriod.end.utc())
    ) {
      return toResult(
        false,
        compileContent(message, {
          endDate: currentFundingPeriod.end.utc().format('MM/DD/YYYY'),
        })
      );
    }
  }

  return toResult(true, 'Is valid date');
};

// args get passed to both rules
const runRuleIf = (input) => {
  const {ifRules, runRules, validationContext} = input;
  let shouldRunRules = true;
  _.forEach(ifRules, (rule) => {
    const ruleResult = runRule(rule, validationContext);
    shouldRunRules = shouldRunRules && ruleResult.isValid;
  });

  let finalResult = toResult(true, 'All valid.');
  if (shouldRunRules) {
    _.forEach(runRules, (rule) => {
      const result = runRule(rule, validationContext);
      if (!result.isValid) {
        finalResult = result;
        return false;
      }
    });
  } else {
    finalResult = toResult(true, 'ifRules are not all true, did not run runRules rules.');
  }
  return finalResult;
};

// to be used in conjunction with validateNestedRules
// uses the data from the array for ifRules and runRules
const nestedRunRuleIf = (input) => {
  const {ifRules, runRules, data} = input;

  let shouldRunRules = true;
  _.forEach(ifRules, (rule) => {
    const ruleResult = {
      isValid: ruleSet[rule.rule]({
        ...rule,
        data: _.get(data, rule.data),
      }).isValid,
    };
    shouldRunRules = shouldRunRules && ruleResult.isValid;
  });

  let finalResult = toResult(true, 'All valid.');
  if (shouldRunRules) {
    _.forEach(runRules, (rule) => {
      const result = {
        isValid: ruleSet[rule.rule]({
          ...rule,
          data: _.get(data, rule.data),
        }).isValid,
        message: rule.message,
      };

      if (!result.isValid) {
        finalResult = result;
        return finalResult;
      }
    });
  } else {
    finalResult = toResult(true, 'ifRules are not all true, did not run runRules rules.');
  }
  return finalResult;
};

// theseRules all true or thoseRules all true
const eitherOr = (input) => {
  const {runRules, validationContext} = input;

  let passed = true;
  let message = 'All rules must be passed.';

  _.forEach(runRules, (rule) => {
    const result = runRule(rule, validationContext);
    passed = passed && result.isValid;

    if (result.isValid) {
      passed = true;
      return false;
    } else {
      passed = false;
    }
    message = result.message;
  });

  return toResult(passed, message);
};

// really just used to group multiple rules under one message
const runRules = (input) => {
  const {rules, message, validationContext} = input;

  let allPass = true;
  _.forEach(rules, (rule) => {
    allPass = allPass && runRule(rule, validationContext).isValid;
  });

  return toResult(allPass, allPass ? 'All rules pass' : message);
};

const runRuleSingle = (input) => {
  const {rule, validationContext} = input;

  return runRule(rule, validationContext);
};

const roundToNearestPenny = (amount) => {
  return Math.ceil(amount * 100) / 100;
};
const findFundingStreamInFilterSubAwards = (filterSubAwards, data) => {
  let result = {};
  _.forEach(filterSubAwards, (subAward) => {
    let found = false;
    _.forEach(subAward.payload.fundingStreams, (stream) => {
      _.forIn(stream.children, (child) => {
        if (_.isEqual(_.keys(child.children), _.keys(data.children))) {
          found = true;
        }
      });
    });
    if (found) {
      result = subAward;
      found = false;
    }
  });
  return result;
};
const runManagementCostFivePercentRuleForFilterSubAwards = (input) => {
  const filterSubAwards = _.get(input, input.fsDataPath);
  const data = input.data;
  let subAwardFromFilter = findFundingStreamInFilterSubAwards(filterSubAwards, data);
  input = {
    ...input,
    subAwardFromFilter,
    fsDataPath: `subAwardFromFilter.payload.fundingStreams`,
  };
  return runManagementCostFivePercentRule(input);
};

const checkLockInAmounts = ({
  subGrantId,
  subApplicationTypeDesc,
  fedShare,
  lockInAmounts,
  subGrants,
  subAmendmentRecommendations,
  subAwardRecommendations,
}) => {
  const lockInLimit = _.toNumber(
    lockInAmounts[
      subApplicationTypeDesc === 'Management cost'
        ? 'managementCostsObligationLimit'
        : 'projectFundsObligationLimit'
    ]
  );

  const validSubGrants = subGrants
    .filter(
      (subGrant) =>
        subGrant.status !== 'subAwardRescinded' &&
        subGrant.subGrantId !== subGrantId &&
        subGrant.payload.applicationTypeDescription === subApplicationTypeDesc
    )
    .map((subGrant) => subGrant.subGrantId);

  const validSubAmendmentRecommendations = [];
  subAmendmentRecommendations
    .sort((a, b) => {
      return new Date(b.modifyDate) - new Date(a.modifyDate);
    })
    .forEach((subAmendmentRecommendation) => {
      if (
        validSubGrants.includes(subAmendmentRecommendation.subGrantId) &&
        (subAmendmentRecommendation.payload.reviewPhase >= 7 ||
          subAmendmentRecommendation.status === 'approved') &&
        _.get(
          subAmendmentRecommendation,
          'payload.review.changeTypes.scopeOfWork',
          _.get(subAmendmentRecommendation, 'payload.scopeOfWork', false)
        ) &&
        !validSubAmendmentRecommendations
          .map((subAmendmentRecommendation) => subAmendmentRecommendation.subGrantId)
          .includes(subAmendmentRecommendation.subGrantId)
      ) {
        validSubAmendmentRecommendations.push(subAmendmentRecommendation);
      }
    });

  const validSubAwardRecommendations = [];
  subAwardRecommendations
    .sort((a, b) => {
      return new Date(b.modifyDate) - new Date(a.modifyDate);
    })
    .forEach((subAwardRecommendation) => {
      if (
        !validSubAmendmentRecommendations
          .map((subAmendmentRecommendation) => subAmendmentRecommendation.subGrantId)
          .includes(subAwardRecommendation.subGrantId) &&
        validSubGrants.includes(subAwardRecommendation.subGrantId) &&
        subAwardRecommendation.payload.reviewPhase >= 7 &&
        !validSubAwardRecommendations
          .map((subAwardRecommendation) => subAwardRecommendation.subGrantId)
          .includes(subAwardRecommendation.subGrantId)
      ) {
        validSubAwardRecommendations.push(subAwardRecommendation);
      }
    });

  const amendmentObligations = validSubAmendmentRecommendations.reduce((acc, cv) => {
    const fedShare =
      cv.payload.strategicFundsMgmt && cv.payload.strategicFundsMgmt.amount
        ? _.toNumber(cv.payload.strategicFundsMgmt.amount)
        : _.get(cv, 'payload.grantAmendment.budget.share.fedShare', []).reduce(
            (acc, cv) => acc + _.toNumber(cv),
            0
          );

    return acc + fedShare;
  }, 0);

  const subAwardObligations = validSubAwardRecommendations.reduce((acc, cv) => {
    const fedShare =
      cv.payload.strategicFundsMgmt && cv.payload.strategicFundsMgmt.amount
        ? _.toNumber(cv.payload.strategicFundsMgmt.amount)
        : _.get(cv, 'payload.budget.share.fedShare', []).reduce(
            (acc, cv) => acc + _.toNumber(cv),
            0
          );

    return acc + fedShare;
  }, 0);

  return fedShare + amendmentObligations + subAwardObligations <= lockInLimit;
};

const runLockInAmountsRule = ({
  subGrantId,
  subApplicationTypeDesc,
  fedShare,
  sfmAmount,
  lockInAmounts,
  subGrants,
  subAmendmentRecommendations,
  subAwardRecommendations,
  message,
}) => {
  return toResult(
    checkLockInAmounts({
      subGrantId,
      subApplicationTypeDesc,
      fedShare: sfmAmount
        ? _.toNumber(sfmAmount)
        : fedShare.reduce((acc, amount) => acc + _.toNumber(amount), 0),
      lockInAmounts: getFirstNonNullKeyInObject(lockInAmounts, [
        'recalculation',
        '12_months',
        '6_months',
        '30_days',
      ]),
      subGrants: subGrants ? Object.values(_.omit(subGrants, 'loading')) : [],
      subAmendmentRecommendations: subAmendmentRecommendations
        ? Object.values(_.omit(subAmendmentRecommendations, 'loading'))
        : [],
      subAwardRecommendations: subAwardRecommendations
        ? Object.values(_.omit(subAwardRecommendations, 'loading'))
        : [],
    }),
    message
  );
};

const runMultipleLockInAmountsRule = ({
  foOrgData,
  selectedApplications,
  subGrants,
  subAmendmentRecommendations,
  subAwardRecommendations,
  message,
}) => {
  foOrgData = foOrgData ? Object.values(_.omit(foOrgData, 'loading')) : [];
  subGrants = subGrants ? Object.values(_.omit(subGrants, 'loading')) : [];
  subAmendmentRecommendations = subAmendmentRecommendations
    ? Object.values(_.omit(subAmendmentRecommendations, 'loading'))
    : [];
  subAwardRecommendations = subAwardRecommendations
    ? Object.values(_.omit(subAwardRecommendations, 'loading'))
    : [];

  const lockInAmounts = foOrgData.map((foOrgData) =>
    getFirstNonNullKeyInObject(foOrgData.data.lockInAmounts, [
      'recalculation',
      '12_months',
      '6_months',
      '30_days',
    ])
  );

  const fedShare = {
    managementCost: 0,
    nonManagementCost: 0,
  };

  _.forEach(selectedApplications, (application, index) => {
    const applicationSubAwardRecs = subAwardRecommendations[index]
      ? subAwardRecommendations[index]
      : [];
    const subAwardRecommendation = applicationSubAwardRecs.find(
      (subAwardRecommendation) =>
        subAwardRecommendation.subGrantId === application.subGrantId
    );
    const sfmAmount = _.get(
      subAwardRecommendation,
      'payload.strategicFundsMgmt.amount',
      null
    );

    fedShare[
      application.subApplicationTypeDesc === 'Management cost'
        ? 'managementCost'
        : 'nonManagementCost'
    ] += sfmAmount
      ? _.toNumber(sfmAmount)
      : application.subApplicationFederalShare
      ? _.toNumber(application.subApplicationFederalShare)
      : 0;
  });

  let isValid = true;
  _.forEach(selectedApplications, (application, index) => {
    if (
      !checkLockInAmounts({
        subGrantId: application.subGrantId,
        subApplicationTypeDesc: application.subApplicationTypeDesc,
        fedShare:
          application.subApplicationTypeDesc === 'Management cost'
            ? fedShare.managementCost
            : fedShare.nonManagementCost,
        lockInAmounts: lockInAmounts[index] ? lockInAmounts[index] : {},
        subGrants: subGrants[index] ? subGrants[index] : [],
        subAmendmentRecommendations: subAmendmentRecommendations[index]
          ? [...subAmendmentRecommendations[index]]
          : [],
        subAwardRecommendations: subAwardRecommendations[index]
          ? [...subAwardRecommendations[index]]
          : [],
      })
    ) {
      isValid = false;
      return false;
    }
  });

  return toResult(isValid, message);
};

const checkFundingCategoryTargets = ({
  subGrantId,
  subApplicationFundingCategory,
  fedShare,
  lockInAmounts,
  fundingCategoryTargets,
  fundingCategories,
  subGrants,
  subAmendmentRecommendations,
  subAwardRecommendations,
}) => {
  const targetAmount =
    _.toNumber(
      lockInAmounts[
        fundingCategoryTargets[
          fundingCategories
            .map((fundingCategory) => fundingCategory.name)
            .indexOf(subApplicationFundingCategory)
        ].calculateAgainst
      ]
    ) *
    (_.toNumber(
      fundingCategoryTargets[
        fundingCategories
          .map((fundingCategory) => fundingCategory.name)
          .indexOf(subApplicationFundingCategory)
      ].targetPercent
    ) /
      100);

  const validSubGrants = subGrants
    .filter(
      (subGrant) =>
        subGrant.status !== 'subAwardRescinded' &&
        subGrant.subGrantId !== subGrantId &&
        subGrant.payload.fundingCategory === subApplicationFundingCategory
    )
    .map((subGrant) => subGrant.subGrantId);

  const validSubAmendmentRecommendations = [];
  subAmendmentRecommendations
    .sort((a, b) => {
      return new Date(b.modifyDate) - new Date(a.modifyDate);
    })
    .forEach((subAmendmentRecommendation) => {
      if (
        validSubGrants.includes(subAmendmentRecommendation.subGrantId) &&
        (subAmendmentRecommendation.payload.reviewPhase >= 7 ||
          subAmendmentRecommendation.status === 'approved') &&
        _.get(
          subAmendmentRecommendation,
          'payload.review.changeTypes.scopeOfWork',
          _.get(subAmendmentRecommendation, 'payload.scopeOfWork', false)
        ) &&
        !validSubAmendmentRecommendations
          .map((subAmendmentRecommendation) => subAmendmentRecommendation.subGrantId)
          .includes(subAmendmentRecommendation.subGrantId)
      ) {
        validSubAmendmentRecommendations.push(subAmendmentRecommendation);
      }
    });

  const validSubAwardRecommendations = [];
  subAwardRecommendations
    .sort((a, b) => {
      return new Date(b.modifyDate) - new Date(a.modifyDate);
    })
    .forEach((subAwardRecommendation) => {
      if (
        !validSubAmendmentRecommendations
          .map((subAmendmentRecommendation) => subAmendmentRecommendation.subGrantId)
          .includes(subAwardRecommendation.subGrantId) &&
        validSubGrants.includes(subAwardRecommendation.subGrantId) &&
        subAwardRecommendation.payload.reviewPhase >= 7 &&
        !validSubAwardRecommendations
          .map((subAwardRecommendation) => subAwardRecommendation.subGrantId)
          .includes(subAwardRecommendation.subGrantId)
      ) {
        validSubAwardRecommendations.push(subAwardRecommendation);
      }
    });

  const amendmentObligations = validSubAmendmentRecommendations.reduce((acc, cv) => {
    const fedShare =
      cv.payload.strategicFundsMgmt && cv.payload.strategicFundsMgmt.amount
        ? _.toNumber(cv.payload.strategicFundsMgmt.amount)
        : _.get(cv, 'payload.grantAmendment.budget.share.fedShare', []).reduce(
            (acc, cv) => acc + _.toNumber(cv),
            0
          );

    return acc + fedShare;
  }, 0);

  const subAwardObligations = validSubAwardRecommendations.reduce((acc, cv) => {
    const fedShare =
      cv.payload.strategicFundsMgmt && cv.payload.strategicFundsMgmt.amount
        ? _.toNumber(cv.payload.strategicFundsMgmt.amount)
        : _.get(cv, 'payload.budget.share.fedShare', []).reduce(
            (acc, cv) => acc + _.toNumber(cv),
            0
          );

    return acc + fedShare;
  }, 0);

  return fedShare + amendmentObligations + subAwardObligations <= targetAmount;
};

const runFundingCategoryTargetsRule = ({
  subGrantId,
  subApplicationFundingCategory,
  fedShare,
  sfmAmount,
  lockInAmounts,
  fundingCategoryTargets,
  fundingCategories,
  subGrants,
  subAmendmentRecommendations,
  subAwardRecommendations,
  message,
}) => {
  return toResult(
    checkFundingCategoryTargets({
      subGrantId,
      subApplicationFundingCategory,
      fedShare: sfmAmount
        ? _.toNumber(sfmAmount)
        : fedShare.reduce((acc, amount) => acc + _.toNumber(amount), 0),
      lockInAmounts: getFirstNonNullKeyInObject(lockInAmounts, [
        'recalculation',
        '12_months',
        '6_months',
        '30_days',
      ]),
      fundingCategoryTargets,
      fundingCategories,
      subGrants: subGrants ? Object.values(_.omit(subGrants, 'loading')) : [],
      subAmendmentRecommendations: subAmendmentRecommendations
        ? Object.values(_.omit(subAmendmentRecommendations, 'loading'))
        : [],
      subAwardRecommendations: subAwardRecommendations
        ? Object.values(_.omit(subAwardRecommendations, 'loading'))
        : [],
    }),
    message
  );
};

const runMultipleFundingCategoryTargetsRule = ({
  foOrgData,
  fundingCategories,
  selectedApplications,
  subGrants,
  subAmendmentRecommendations,
  subAwardRecommendations,
  message,
}) => {
  foOrgData = foOrgData ? Object.values(_.omit(foOrgData, 'loading')) : [];
  subGrants = subGrants ? Object.values(_.omit(subGrants, 'loading')) : [];
  subAmendmentRecommendations = subAmendmentRecommendations
    ? Object.values(_.omit(subAmendmentRecommendations, 'loading'))
    : [];
  subAwardRecommendations = subAwardRecommendations
    ? Object.values(_.omit(subAwardRecommendations, 'loading'))
    : [];
  const lockInAmounts = foOrgData.map((foOrgData) =>
    getFirstNonNullKeyInObject(foOrgData.data.lockInAmounts, [
      'recalculation',
      '12_months',
      '6_months',
      '30_days',
    ])
  );
  const fundingCategoryTargets = foOrgData.map(
    (foOrgData) => foOrgData.data.fundingCategoryTargets
  );

  const fedShare = {};

  fundingCategories.map((fundingCategory) => {
    fedShare[fundingCategory.name] = 0;
  });

  _.forEach(selectedApplications, (application, index) => {
    const applicationSubAwardRecs = subAwardRecommendations[index]
      ? subAwardRecommendations[index]
      : [];
    const subAwardRecommendation = applicationSubAwardRecs.find(
      (subAwardRecommendation) =>
        subAwardRecommendation.subGrantId === application.subGrantId
    );
    const sfmAmount = _.get(
      subAwardRecommendation,
      'payload.strategicFundsMgmt.amount',
      null
    );

    fedShare[application.subApplicationFundingCategory] += sfmAmount
      ? _.toNumber(sfmAmount)
      : application.subApplicationFederalShare
      ? _.toNumber(application.subApplicationFederalShare)
      : 0;
  });

  let isValid = true;
  _.forEach(selectedApplications, (application, index) => {
    if (
      !checkFundingCategoryTargets({
        subGrantId: application.subGrantId,
        subApplicationFundingCategory: application.subApplicationFundingCategory,
        fedShare: fedShare[application.subApplicationFundingCategory],
        lockInAmounts: lockInAmounts[index] ? lockInAmounts[index] : {},
        fundingCategoryTargets: fundingCategoryTargets[index]
          ? fundingCategoryTargets[index]
          : {},
        fundingCategories,
        subGrants: subGrants[index] ? subGrants[index] : [],
        subAmendmentRecommendations: subAmendmentRecommendations[index]
          ? [...subAmendmentRecommendations[index]]
          : [],
        subAwardRecommendations: subAwardRecommendations[index]
          ? [...subAwardRecommendations[index]]
          : [],
      })
    ) {
      isValid = false;
      return false;
    }
  });

  return toResult(isValid, message);
};

const checkSRMCLimit = ({
  subGrantId,
  lockInAmounts,
  federalShareCostSharePrimeAward,
  subGrants,
  subAmendmentRecommendations,
  subAwardRecommendations,
  managementCosts,
}) => {
  // We have two entry points for the federalShareCostSharePrimeAward value, one that enters a whole number and one that enters decimals.
  // Want to make sure we are doing the calculation with a decimal version
  // If federalShareCostSharePrimeAward is blank/undefined we just return 0.00 instead of trying to divide by 0
  if (!federalShareCostSharePrimeAward) {
    return false;
  }

  let convertedFedCostShare = _.toNumber(federalShareCostSharePrimeAward);
  let consumedFederalShareCostSharePrimeAward =
    convertedFedCostShare <= 1 ? convertedFedCostShare : convertedFedCostShare / 100;

  const srmcLimit =
    0.05 *
    (_.toNumber(lockInAmounts.projectFundsObligationLimit) /
      consumedFederalShareCostSharePrimeAward);

  const validSubGrants = subGrants
    .filter(
      (subGrant) =>
        subGrant.status !== 'subAwardRescinded' &&
        subGrant.subGrantId !== subGrantId &&
        subGrant.payload.applicationTypeDescription !== 'Management cost'
    )
    .map((subGrant) => subGrant.subGrantId);

  const validSubAmendmentRecommendations = [];
  subAmendmentRecommendations
    .sort((a, b) => {
      return new Date(b.modifyDate) - new Date(a.modifyDate);
    })
    .forEach((subAmendmentRecommendation) => {
      if (
        validSubGrants.includes(subAmendmentRecommendation.subGrantId) &&
        (subAmendmentRecommendation.payload.reviewPhase >= 7 ||
          subAmendmentRecommendation.status === 'approved') &&
        _.get(
          subAmendmentRecommendation,
          'payload.review.changeTypes.scopeOfWork',
          _.get(subAmendmentRecommendation, 'payload.scopeOfWork', false)
        ) &&
        !validSubAmendmentRecommendations
          .map((subAmendmentRecommendation) => subAmendmentRecommendation.subGrantId)
          .includes(subAmendmentRecommendation.subGrantId)
      ) {
        validSubAmendmentRecommendations.push(subAmendmentRecommendation);
      }
    });

  const validSubAwardRecommendations = [];
  subAwardRecommendations
    .sort((a, b) => {
      return new Date(b.modifyDate) - new Date(a.modifyDate);
    })
    .forEach((subAwardRecommendation) => {
      if (
        !validSubAmendmentRecommendations
          .map((subAmendmentRecommendation) => subAmendmentRecommendation.subGrantId)
          .includes(subAwardRecommendation.subGrantId) &&
        validSubGrants.includes(subAwardRecommendation.subGrantId) &&
        subAwardRecommendation.payload.reviewPhase >= 7 &&
        !validSubAwardRecommendations
          .map((subAwardRecommendation) => subAwardRecommendation.subGrantId)
          .includes(subAwardRecommendation.subGrantId)
      ) {
        validSubAwardRecommendations.push(subAwardRecommendation);
      }
    });

  const amendmentManagementCosts = validSubAmendmentRecommendations.reduce((acc, cv) => {
    const totalAmounts1 = _.get(cv, 'payload.grantAmendment.budget.share.totalAmounts[1]');
    const managementCosts = totalAmounts1 ? _.toNumber(totalAmounts1) : 0;

    return acc + managementCosts;
  }, 0);

  const subAwardManagementCosts = validSubAwardRecommendations.reduce((acc, cv) => {
    const totalAmounts1 = _.get(cv, 'payload.budget.share.totalAmounts[1]');
    const managementCosts = totalAmounts1 ? _.toNumber(totalAmounts1) : 0;

    return acc + managementCosts;
  }, 0);

  return (
    managementCosts + amendmentManagementCosts + subAwardManagementCosts <= srmcLimit
  );
};

const runSRMCLimitRule = ({
  subGrantId,
  federalShareCostSharePrimeAward,
  managementCosts,
  lockInAmounts,
  subGrants,
  subAmendmentRecommendations,
  subAwardRecommendations,
  message,
}) => {
  return toResult(
    checkSRMCLimit({
      subGrantId,
      lockInAmounts: getFirstNonNullKeyInObject(lockInAmounts, [
        'recalculation',
        '12_months',
        '6_months',
        '30_days',
      ]),
      federalShareCostSharePrimeAward,
      subGrants: subGrants ? Object.values(_.omit(subGrants, 'loading')) : [],
      subAmendmentRecommendations: subAmendmentRecommendations
        ? Object.values(_.omit(subAmendmentRecommendations, 'loading'))
        : [],
      subAwardRecommendations: subAwardRecommendations
        ? Object.values(_.omit(subAwardRecommendations, 'loading'))
        : [],
      managementCosts: managementCosts ? _.toNumber(managementCosts) : 0,
    }),
    message
  );
};

const runMultipleSRMCLimitRule = ({
  federalShareCostSharePrimeAward,
  foOrgData,
  selectedApplications,
  subGrants,
  subAmendmentRecommendations,
  subAwardRecommendations,
  message,
}) => {
  foOrgData = foOrgData ? Object.values(_.omit(foOrgData, 'loading')) : [];
  const lockInAmounts = foOrgData.map((foOrgData) =>
    getFirstNonNullKeyInObject(foOrgData.data.lockInAmounts, [
      'recalculation',
      '12_months',
      '6_months',
      '30_days',
    ])
  );
  subGrants = subGrants ? Object.values(_.omit(subGrants, 'loading')) : [];
  subAmendmentRecommendations = subAmendmentRecommendations
    ? Object.values(_.omit(subAmendmentRecommendations, 'loading'))
    : [];
  subAwardRecommendations = subAwardRecommendations
    ? Object.values(_.omit(subAwardRecommendations, 'loading'))
    : [];

  let managementCosts = 0;

  _.forEach(selectedApplications, (application, index) => {
    if (application.subApplicationTypeDesc !== 'Management cost') {
      const subAwardRecommendation = (subAwardRecommendations[index]
        ? subAwardRecommendations[index]
        : []
      ).find(
        (subAwardRecommendation) =>
          subAwardRecommendation.subGrantId === application.subGrantId
      );

      const applicationManagementCosts = subAwardRecommendation
        ? subAwardRecommendation.payload.budget.share.totalAmounts[1]
        : 0;

      managementCosts += applicationManagementCosts
        ? _.toNumber(applicationManagementCosts)
        : 0;
    }
  });

  let isValid = true;
  _.forEach(selectedApplications, (application, index) => {
    if (application.subApplicationTypeDesc !== 'Management cost') {
      if (
        !checkSRMCLimit({
          subGrantId: application.subGrantId,
          lockInAmounts: lockInAmounts[index] ? lockInAmounts[index] : {},
          federalShareCostSharePrimeAward,
          subGrants: subGrants[index] ? subGrants[index] : [],
          subAmendmentRecommendations: subAmendmentRecommendations[index]
            ? [...subAmendmentRecommendations[index]]
            : [],
          subAwardRecommendations: subAwardRecommendations[index]
            ? [...subAwardRecommendations[index]]
            : [],
          managementCosts,
        })
      ) {
        isValid = false;
        return false;
      }
    }
  });

  return toResult(isValid, message);
};

const runManagementCostFivePercentRule = (input) => {
  const fsDataPath = _.get(
    input,
    'fsDataPath',
    'validationContext.data.payload.fundingStreams'
  );
  const mode = _.get(input, 'mode', 'application');
  const data = _.get(input, fsDataPath);
  var totalProjectCosts = 0;
  var totalManagementCosts = 0;
  _.forEach(data, (child) => {
    const children = _.get(child, 'children', {});
    _.forEach(children, (item) => {
      if (item.itemCode === 418) {
        totalProjectCosts = calculateChildrenTotalByMode(item.children, mode);
      }
      if (item.itemCode === 419) {
        totalManagementCosts = calculateChildrenTotalByMode(item.children, mode);
      }
    });
  });
  const isValid =
    roundToNearestPenny(totalManagementCosts) <=
    roundToNearestPenny(totalProjectCosts * 0.05)
      ? true
      : false;
  return {
    isValid: isValid,
    message: input.message,
    path: 'costTypeError',
    customText: `(Maximum allowable cost: ${showDollarAmountWithDollar(
      roundToNearestPenny(totalProjectCosts * 0.05)
    )})`,
  };
};

const runManagementCostFivePercentRuleProgramIncome = (input) => {
  const fsDataPath = _.get(
    input,
    'fsDataPath',
    'validationContext.data.payload.fundingStreams'
  );

  const programIncomePath = _.get(
    input,
    'programIncome',
    'validationContext.data.payload.budget.programIncome'
  );

  const mode = _.get(input, 'mode', 'application');
  const data = _.get(input, fsDataPath);
  var programIncome = _.get(input, programIncomePath, 0);
  var totalProjectCosts = 0;
  var totalManagementCosts = 0;
  _.forEach(data, (child) => {
    const children = _.get(child, 'children', {});
    _.forEach(children, (item) => {
      if (item.itemCode === 418) {
        totalProjectCosts = calculateChildrenTotalByMode(item.children, mode);
        totalProjectCosts -= _.toNumber(programIncome !== '' ? programIncome : 0);
      }
      if (item.itemCode === 419) {
        totalManagementCosts = calculateChildrenTotalByMode(item.children, mode);
      }
    });
  });

  const isValid =
    roundToNearestPenny(totalManagementCosts) <=
    roundToNearestPenny(totalProjectCosts * 0.05)
      ? true
      : false;

  return {
    isValid: isValid,
    message: input.message,
    path: 'costTypeError',
    customText: `(Maximum allowable cost: ${showDollarAmountWithDollar(
      roundToNearestPenny(totalProjectCosts * 0.05)
    )})`,
  };
};

const managementCostMaxPercentageThresholdRule = (input) => {
  const fsDataPath = _.get(
    input,
    'fsDataPath',
    'validationContext.data.payload.fundingStreams'
  );

  const programIncomePath = _.get(
    input,
    'programIncome',
    'validationContext.data.payload.budget.programIncome'
  );

  const mode = _.get(input, 'mode', 'application');
  const data = _.get(input, fsDataPath);
  const percentageParam = _.get(input, 'maxPercentageOfManagementCost', 5);
  var programIncome = _.get(input, programIncomePath, 0);
  var totalProjectCosts = 0;
  var totalManagementCosts = 0;
  _.forEach(data, (child) => {
    const children = _.get(child, 'children', {});
    _.forEach(children, (item) => {
      if (item.itemCode === 418) {
        totalProjectCosts = calculateChildrenTotalByMode(item.children, mode);
        totalProjectCosts -= _.toNumber(programIncome !== '' ? programIncome : 0);
      }
      if (item.itemCode === 419) {
        totalManagementCosts = calculateChildrenTotalByMode(item.children, mode);
      }
    });
  });

  const percentage = percentageParam * 0.01;

  const isValid =
    roundToNearestPenny(totalManagementCosts) <=
    roundToNearestPenny(totalProjectCosts * percentage)
      ? true
      : false;

  return {
    isValid: isValid,
    message: input.message,
    path: 'costTypeError',
    customText: `(Maximum allowable cost: ${showDollarAmountWithDollar(
      roundToNearestPenny(totalProjectCosts * percentage)
    )})`,
  };
};

const runCostRules = (input) => {
  const {fieldValidations, validationContext, data: costs} = input;
  let resultError = {};
  let allValid = true;

  _.forEach(costs, (costPeriod, index) => {
    _.forEach(costPeriod, (cost, costIndex) => {
      _.forEach(fieldValidations, (validations, field) => {
        let validationContextWithFieldPath = {
          ...validationContext,
          root: cost,
          yearIndex: index,
          costIndex: costIndex,
        };

        _.forEach(validations, (validation, valIndex) => {
          const result = runRule(validation, validationContextWithFieldPath);
          _.set(resultError, `${field}.${index}.${costIndex}.${valIndex}`, result);
          allValid = allValid && result.isValid;
        });
      });
    });
  });
  resultError.isValid = allValid;
  return resultError;
};

const validPhoneNumber = (input) => {
  const {data, message} = input;
  const phoneNo = /^\d{10}$/;

  let result = !data || !!data.match(phoneNo);
  return toResult(result, message);
};

const validPhoneExtension = (input) => {
  const {data, message} = input;
  const phoneExt = /^\d{1,6}$/;

  let result = !data || !!data.match(phoneExt);
  return toResult(result, message);
};

const validEmail = (input) => {
  const {data, message} = input;
  const email = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,4})+$/;

  let result = !data || !!email.test(data);
  return toResult(result, message);
};

const noDuplicateContactPhoneNumbers = (input) => {
  const {contact, otherContacts, message} = input;

  let phoneNumbers = [];
  _.forEach(otherContacts, (contact) => {
    if (!checkIfValuesAre([null, undefined, ''], _.get(contact, 'phoneNumber'))) {
      phoneNumbers.push({
        phoneNumber: _.get(contact, 'phoneNumber'),
        extension: _.get(contact, 'extension'),
      });
    }

    if (
      !checkIfValuesAre([null, undefined, ''], _.get(contact, 'secondaryPhoneNumber'))
    ) {
      phoneNumbers.push({
        phoneNumber: _.get(contact, 'secondaryPhoneNumber'),
        extension: _.get(contact, 'secondaryPhoneExt'),
      });
    }

    if (!checkIfValuesAre([null, undefined, ''], _.get(contact, 'optionalPhoneNumber'))) {
      phoneNumbers.push({
        phoneNumber: _.get(contact, 'optionalPhoneNumber'),
        extension: _.get(contact, 'optionalPhoneExt'),
      });
    }
  });

  const contactPhoneNumber = _.toString(_.get(contact, 'phoneNumber'));
  const contactExtension = _.toString(_.get(contact, 'extension'));

  const hasDuplicates = _.some(phoneNumbers, (number) => {
    const {phoneNumber, extension} = number;
    return (
      _.isEqual(contactPhoneNumber, _.toString(phoneNumber)) &&
      _.isEqual(contactExtension, _.toString(extension))
    );
  });

  const result = contactPhoneNumber === '' || !hasDuplicates; // only check for duplicate if it's not undefined so we don't have empty, optional fields conflict

  return toResult(result, message);
};

const noDuplicateEmail = (input) => {
  const {data, message} = input;
  let emails = [];
  _.forEach(data, (contact) => {
    if (_.get(contact, 'email', undefined)) {
      emails.push(contact.email);
    }
  });
  let result = _.size(new Set(emails)) === _.size(emails);
  return toResult(result, message);
};

const mustBeInRange = ({data, minimum, maximum, message}) => {
  let result = data > minimum && data < maximum;
  return toResult(result, message);
};

const mustBeInRangeInclusive = ({data, minimum, maximum, message, ranges}) => {
  let data_num = _.toNumber(data);
  let minimum_num = _.toNumber(minimum);
  let maximum_num = _.toNumber(maximum);
  let result;
  const coversMinMaxRange = ({minimum, maximum}) =>
    minimum <= data_num && data_num <= maximum;
  if (!ranges) result = data_num >= minimum_num && data_num <= maximum_num;
  else result = _.some(ranges, coversMinMaxRange);
  return toResult(result, message);
};

// runs validations for each field in object (more flexible than validateNestedRules, but less simplified syntax)
// allows arbitrary arguments and dependencies
const validateObject = (input) => {
  const {fieldValidations, dataPath, validationContext} = input;

  let resultError = {};
  let allValid = true;

  _.forEach(fieldValidations, (validations, field) => {
    let validationContextWithFieldPath = {
      ...validationContext,
      fieldPath: `${dataPath}.${field}`,
    };

    _.forEach(validations, (validation, index) => {
      const result = runRule(validation, validationContextWithFieldPath);
      _.set(resultError, `${field}.${index}`, result);
      allValid = allValid && result.isValid;
    });
  });
  resultError.isValid = allValid;
  return resultError;
};

const hasValidateMaxOccursToggle = (validationContext) => {
  return _.includes(
    _.get(validationContext, 'context.toggles.toggles'),
    'addMaxOccursValidation'
  );
};

const getFundingOpportunityConfigFundingStream = (
  validationContext,
  fundingStreamType
) => {
  return _.find(
    _.get(validationContext, 'context.fundingOpportunityV2.fundingStream'),
    (item) => _.get(item, 'fundingStreamType') === fundingStreamType
  );
};

const getFundingOpportunityConfigChildItem = (configItem, itemCode) => {
  return _.find(
    _.get(configItem, 'items', _.get(configItem, 'children', [])),
    (child) => _.get(child, 'itemCode') === itemCode
  );
};

const runRulesRDHelper = (item, currRulesConfig, validationContext) => {
  let applyToAllChildRulesConfig = _.get(currRulesConfig, 'allChildren'); // applicable to all item codes at this level
  let groupSpecificRuleConfig = _.find(
    _.get(currRulesConfig, 'childrenGroups'),
    (group) => _.includes(group.itemCodes, item.itemCode)
  );
  let itemCodeSpecificRuleConfig = _.get(currRulesConfig, [
    'children',
    _.toString(item.itemCode),
  ]); // overrides the applyToAllChildRules
  let childRulesConfigToUse =
    itemCodeSpecificRuleConfig !== undefined
      ? itemCodeSpecificRuleConfig
      : groupSpecificRuleConfig !== undefined
      ? groupSpecificRuleConfig
      : applyToAllChildRulesConfig;

  let childRules = _.get(childRulesConfigToUse, 'rules');
  const results = {};
  let found;
  const addMaxOccursValidation = hasValidateMaxOccursToggle(validationContext);
  if (addMaxOccursValidation) {
    found = {};
  }
  _.forEach(item.children, (child, childKey) => {
    let validationContextWithRoot = {
      ...validationContext,
      root: _.get(validationContext, ['root', 'children', childKey]),
      parentRoot: _.get(validationContext, ['root']),
    };
    if (addMaxOccursValidation) {
      validationContextWithRoot.fundingOpportunityItemConfig = getFundingOpportunityConfigChildItem(
        _.get(validationContext, 'fundingOpportunityItemConfig', {}),
        _.get(child, 'itemCode')
      );
      let validationResult = runRulesRDHelper(
        child,
        childRulesConfigToUse,
        validationContextWithRoot
      );
      if (_.get(child, 'itemCode')) {
        found[child.itemCode] = (found[child.itemCode] || 0) + 1;
        let thisMaxOccurs = _.get(
          validationContextWithRoot,
          'fundingOpportunityItemConfig.maxOccurs'
        );
        if (thisMaxOccurs && thisMaxOccurs < found[child.itemCode]) {
          // error too many
          validationResult = validationResult || {};
          validationResult.error = validationResult.error || [];
          let tooManyError = {
            isValid: false,
            message: 'Too Many',
            activeAccordionError: 'Item limit exceeded in current selection.',
          };
          validationResult.error.push(tooManyError);
        }
      }
      _.setWith(results, `children.${childKey}`, validationResult, Object);
      return;
    }

    _.setWith(
      results,
      `children.${childKey}`,
      runRulesRDHelper(child, childRulesConfigToUse, validationContextWithRoot),
      Object
    );
  });

  _.forEach(childRules, (rule, ruleIndex) => {
    _.set(results, [ERROR, ruleIndex], runRule(rule, validationContext));
  });

  return results;
};

const getStreamRules = (fundingStreamType, rules) => {
  return _.find(rules, (rule, ruleName) => fundingStreamType === ruleName);
};

const runStreamRules = (
  stream,
  index,
  validationContext,
  applicantCharacteristics,
  rules,
  entityDataPath,
  data
) => {
  let results = {};
  let fundingOpportunityFundingStreamConfig, found;
  const addMaxOccursValidation = hasValidateMaxOccursToggle(validationContext);
  if (addMaxOccursValidation) {
    fundingOpportunityFundingStreamConfig = getFundingOpportunityConfigFundingStream(
      validationContext,
      _.get(stream, 'fundingStreamType')
    );
    found = {};
  }

  _.forEach(stream.children, (child, childKey) => {
    let validationContextWithRoot = {
      ...validationContext,
      applicantCharacteristics,
      root: _.get(data, [index, 'children', childKey]),
    };
    if (addMaxOccursValidation) {
      validationContextWithRoot.fundingOpportunityItemConfig = getFundingOpportunityConfigChildItem(
        fundingOpportunityFundingStreamConfig,
        _.get(child, 'itemCode')
      );
      let validationResult = runRulesRDHelper(
        child,
        getStreamRules(stream.fundingStreamType, rules),
        validationContextWithRoot
      );
      if (_.get(child, 'itemCode')) {
        found[child.itemCode] = (found[child.itemCode] || 0) + 1;
        let thisMaxOccurs = _.get(
          validationContextWithRoot,
          'fundingOpportunityItemConfig.maxOccurs'
        );
        if (thisMaxOccurs && thisMaxOccurs < found[child.itemCode]) {
          // error too many
          validationResult = validationResult || {};
          validationResult.error = validationResult.error || [];
          let tooManyError = {
            isValid: false,
            message: 'Too Many',
            activeAccordionError: 'Item limit exceeded in current selection.',
          };
          validationResult.error.push(tooManyError);
        }
      }
      _.set(results, `${entityDataPath}.${index}.children.${childKey}`, validationResult);
      return;
    }
    _.set(
      results,
      `${entityDataPath}.${index}.children.${childKey}`,
      runRulesRDHelper(
        child,
        getStreamRules(stream.fundingStreamType, rules),
        validationContextWithRoot
      )
    );
  });
  return results;
};

const isValid = (obj, result) => {
  if (_.get(obj, 'isValid') === false) {
    return false;
  }
  if (_.isObject(obj)) {
    _.each(_.keys(obj), (inst) => {
      result = result && isValid(obj[inst], result);
    });
  }
  return result;
};

const runCostShareRulesForMultiple = (input) => {
  const {rules, validationContext, data} = input;
  let resultError = {};
  let allValid = true;
  _.forEach(data, (parentStream, index) => {
    let vc = {
      ...validationContext,
      data: parentStream,
    };
    let fieldResultError = {};
    let fieldValid = true;
    Object.keys(rules).forEach((ruleKey, ruleIndex) => {
      const fieldRules = rules[ruleKey];
      let ruleError = {};
      let ruleValid = true;
      _.forEach(fieldRules, (rule, ruleIndex) => {
        const results = runRuleSingle({rule, validationContext: vc});
        _.set(ruleError, `${ruleIndex}`, {...results});
        ruleValid = ruleValid && results.isValid;
      });
      ruleError.isValid = ruleValid;
      _.set(fieldResultError, `${ruleKey}`, {...ruleError});
      fieldValid = fieldValid && ruleValid;
    });
    fieldResultError.isValid = fieldValid;
    _.set(resultError, `${index}`, {...fieldResultError});
    allValid = allValid && fieldValid;
  });
  resultError.isValid = allValid;
  return resultError;
};

const runRulesRequestDetailsForMultiple = (input) => {
  let {rules, validationContext, applicantCharacteristics} = input;
  let results = {};
  _.forEach(input.data, (parentStream, index) => {
    const data = parentStream.payload.fundingStreams;
    const entityDataPath = `${input.entityDataPath}.${index}.payload.fundingStreams`;
    _.forEach(data, (stream, index) => {
      results = _.merge(
        results,
        runStreamRules(
          stream,
          index,
          validationContext,
          applicantCharacteristics,
          rules,
          entityDataPath,
          data
        )
      );
    });
  });

  let returnResults = [];
  returnResults.push(results);

  return {
    isValid: isValid(returnResults, true),
    results: returnResults,
  };
};

const runRulesRequestDetails = (input) => {
  const {
    rules,
    validationContext,
    data,
    entityDataPath,
    applicantCharacteristics,
  } = input;

  let results = {};

  _.forEach(data, (stream, index) => {
    results = _.merge(
      results,
      runStreamRules(
        stream,
        index,
        validationContext,
        applicantCharacteristics,
        rules,
        entityDataPath,
        data
      )
    );
  });

  let returnResults = [];
  returnResults.push(results);

  return {
    isValid: isValid(returnResults, true),
    results: returnResults,
  };
};

const requestDetailsCostItemTotal = (input) => {
  const {data, comparison, message, mode} = input;
  const {
    lessThan,
    greaterThan,
    equalTo,
    greaterThanEqualTo,
    lessThanEqualTo,
  } = comparison;

  const calculatedTotal = calculateChildrenTotalByMode(_.get(data, 'children'), mode);

  let isValid = true;
  if (lessThan !== undefined) {
    isValid = isValid && calculatedTotal < lessThan;
  }
  if (lessThanEqualTo != undefined) {
    isValid = isValid && calculatedTotal <= lessThanEqualTo;
  }
  if (greaterThan !== undefined) {
    isValid = isValid && calculatedTotal > greaterThan;
  }
  if (greaterThanEqualTo != undefined) {
    isValid = isValid && calculatedTotal >= greaterThanEqualTo;
  }
  if (equalTo !== undefined) {
    isValid = isValid && calculatedTotal == equalTo;
  }
  return toResult(isValid, message);
};

export const isCheckedMinimumOneOption = ({data, message}) => {
  let isValid = false;
  if (data === undefined) {
    isValid = false;
  } else {
    _.mapKeys(data, function (value, key) {
      isValid =
        isValid || (_.isObject(value) && !_.isEmpty(value))
          ? true
          : value === true
          ? true
          : false; // some checkboxgroupitem comes as an object
    });
  }
  return toResult(isValid, message);
};

export const ifTrueThenCheckedAllOptions = ({data, options, response, message}) => {
  let isValid = true,
    applyRules;
  if (
    response &&
    (response === 'Yes' || (_.isObject(response) && !_.isEmpty(response)))
  ) {
    applyRules = true;
  } else {
    applyRules = false;
  }
  if (applyRules) {
    if (data !== undefined && Object.keys(data).length === options) {
      _.mapKeys(data, function (value, key) {
        isValid = isValid && value;
      });
    } else {
      isValid = false;
    }
  } else {
    isValid = true;
  }

  return toResult(isValid, message);
};

export const ifTrueThenCheckedMinimumOneOption = ({data, response, message}) => {
  let isValid = true,
    applyRules;

  if (response && (response === true || (_.isObject(response) && !_.isEmpty(response)))) {
    applyRules = true;
  } else {
    applyRules = false;
  }

  if (applyRules) {
    isValid = data !== undefined ? true : false;
    isValid = _.some(_.keys(data), (key) => data[key]);
  } else {
    isValid = true;
  }

  return toResult(isValid, message);
};
export const isGreaterThanOrEqualToArrMax = ({data, message, valueArray, property}) => {
  const maxValue = _.get(
    _.maxBy(valueArray, (obj) => _.toNumber(obj[property])),
    property
  );
  return toResult(
    _.toNumber(data) >= _.toNumber(maxValue) && _.isInteger(_.toNumber(data)),
    message
  );
};

export const isUnique = ({data, dataToCheck, message, criteria, crossStreamCheck}) => {
  const findAll = (unFilteredData, filteredCriteria) => {
    if (_.get(filteredCriteria, 'criteria')) {
      let filteredData;
      if (_.get(filteredCriteria, 'fieldPath')) {
        filteredData = _.flatMap(unFilteredData, (currData) =>
          _.filter(_.get(currData, filteredCriteria.fieldPath), filteredCriteria.filter)
        );
      } else {
        filteredData = _.filter(unFilteredData, filteredCriteria.filter);
      }
      return findAll(filteredData, filteredCriteria.criteria);
    } else {
      return _.filter(unFilteredData, (currData) =>
        _.isEqual(
          _.trim(_.toLower(_.get(currData, _.get(filteredCriteria, 'fieldPath')))),
          _.trim(_.toLower(data))
        )
      );
    }
  };
  let dupThreshold = 2;

  if (crossStreamCheck) {
    dupThreshold = 1;
  }

  return toResult(_.size(findAll(dataToCheck, criteria)) < dupThreshold, message);
};

export const validateProjectName = (props) => {
  const {fundingStreams, data, projectName, message, fundingStreamType} = props;
  const currentFS = _.filter(
    fundingStreams,
    (fs) => fs.fundingStreamType == fundingStreamType
  );
  let projectNames = [];
  _.map(currentFS, (child) => {
    const children = _.get(child, 'children', {});
    let names = _.chain(children)
      .filter(
        (item) =>
          _.lowerCase(
            _.get(
              item,
              `additionalInformation.narrative[${projectName}]`,
              _.get(item, `additionalInformation[${projectName}]`, '')
            )
          ) === _.lowerCase(data)
      )
      .map((item) =>
        _.lowerCase(
          _.get(
            item,
            `additionalInformation.narrative[${projectName}]`,
            _.get(item, `additionalInformation[${projectName}]`, '')
          )
        )
      );
    projectNames = [...projectNames, ...names];
  });
  const hasDuplicates = (projectNames) =>
    new Set(projectNames).size != projectNames.length;
  return toResult(!hasDuplicates(projectNames), message);
};

export const runRulesReviewPanel = ({sections, validationContext}) => {
  let resultError = {};
  let canSubmit = true;
  let canSubmitPositive = true;

  _.forEach(sections, (sectionConfig, sectionName) => {
    let {validRules, positiveSubmissionRules} = sectionConfig;

    let completeSection = true;
    let canSubmitSectionPositive = true;

    _.forEach(validRules, (validations, field) => {
      _.forEach(validations, (validation, index) => {
        const result = runRule(validation, validationContext);
        _.set(resultError, `${sectionName}.${field}.${index}`, result);
        completeSection = completeSection && result.isValid;
      });
    });

    // approved rules are run with the assumption that the section is complete
    if (completeSection) {
      _.forEach(positiveSubmissionRules, (validation) => {
        const result = runRule(validation, validationContext);
        canSubmitSectionPositive = canSubmitSectionPositive && result.isValid;
      });
    } else {
      canSubmitSectionPositive = false;
    }

    // does the section have any validation errors vs is the section green/red
    _.set(resultError, `${sectionName}.isValid`, completeSection);

    _.set(resultError, `${SECTION_RESULTS}.${sectionName}`, {
      [CAN_SUBMIT]: completeSection,
      [CAN_SUBMIT_POSITIVE]: canSubmitSectionPositive,
    });

    canSubmit = canSubmit && completeSection;
    canSubmitPositive = canSubmitPositive && canSubmitSectionPositive;
  });

  resultError.isValid = canSubmit;
  _.set(resultError, 'sectionResults.all', {
    [CAN_SUBMIT]: canSubmit,
    [CAN_SUBMIT_POSITIVE]: canSubmitPositive,
  });

  return resultError;
};

const runRulesScoringController = ({
  scores,
  partsConfig,
  fundingStreams,
  refItems,
  tierToReviewAt = 1,
  validationContext,
}) => {
  let isAllValid = true;
  let resultError = {};
  let sectionResults = {};
  let matchingItems = [];

  _.forEach(partsConfig, (partConfig) => {
    const {factorsConfig, part, partType} = partConfig;

    if (partType === 'item') {
      matchingItems = getBudgetItemsAtTier(
        fundingStreams,
        refItems,
        (item) => _.toString(item.itemCode) === part,
        tierToReviewAt
      );
    } else if (partType === 'custom') {
      matchingItems = [
        {
          itemCode: part,
          itemKey: part,
          itemName: part,
          itemPath: part,
        },
      ];
    }

    _.forEach(matchingItems, (item) => {
      const partId = _.get(item, 'itemPath');
      _.set(sectionResults, [partId, CAN_SUBMIT], true);

      _.forEach(factorsConfig, (factorConfig) => {
        const {validRules, factor} = factorConfig;
        _.set(sectionResults, [partId, factor, CAN_SUBMIT], true);
        const score = _.find(scores, {part: part, partId: partId, factor: factor}) || {};
        let factorErrors = [];

        _.forEach(validRules, (validRule, ruleIndex) => {
          const validationContextWithRoot = {
            ...validationContext,
            root: score,
          };
          const result = runRule(validRule, validationContextWithRoot);

          const factorResult =
            result.isValid && !!_.get(sectionResults, [partId, factor, CAN_SUBMIT]);
          const partResult =
            factorResult && !!_.get(sectionResults, [partId, CAN_SUBMIT]);

          // set section status parameters for part and factor sections
          _.set(sectionResults, [partId, factor, CAN_SUBMIT], factorResult);
          _.set(sectionResults, [partId, factor, CAN_SUBMIT_POSITIVE], true); // for future enhancement of grey/red/grey

          _.set(sectionResults, [partId, CAN_SUBMIT], partResult);
          _.set(sectionResults, [partId, CAN_SUBMIT_POSITIVE], true);

          factorErrors.push(result); // add result to factor results for input fields
          isAllValid = result.isValid && isAllValid;
        });

        // add all factor results for input fields to use
        _.set(resultError, [partId, factor, ERROR], factorErrors);
      });
    });
  });

  return {
    isValid: isAllValid,
    [PART_RESULTS]: resultError,
    [SECTION_RESULTS]: sectionResults,
  };
};

const isAnyDuplicateCombination = (props) => {
  const sets = props.sets;
  const uniqueCombo = new Set();
  _.each(sets, (set) => {
    let record = [];
    _.each(set.set, (key) => {
      if (props.excludeOptions && _.includes(props.excludeOptions, props[key])) {
        record.push(Math.random());
      } else {
        record.push(props[key]);
      }
    });
    uniqueCombo.add(JSON.stringify(record));
  });
  return toResult(sets.length === uniqueCombo.size, props.message);
};

const phasedProjectRequired = ({data, scopeOfWorkPhasedProject, message}) => {
  let isValid = true;
  if (scopeOfWorkPhasedProject === 'No' && (data === 'Phase 1' || data === 'Phase 2')) {
    return toResult(!isValid, message);
  } else {
    return toResult(isValid, message);
  }
};

const validateSingleEntry = ({data, message, header}) => {
  return toResult(!(_.size(data) > 1), message, header);
};

export const isFieldEqualInObjectList = (props) => {
  const {value, fieldName, compareValue, compareFieldName, objectsList, message} = props;
  const objectToCheck = _.find(objectsList, (option) => option[fieldName] == value);
  if (objectToCheck) {
    if (!compareFieldName) {
      return toResult(true, message);
    }
    const valueToCheck = _.get(objectToCheck, compareFieldName);
    return toResult(_.isEqual(valueToCheck, compareValue), message);
  }
  return toResult(false, message);
};

const checkIfBsirExistForReportingPeriodWithStatus = ({
  data,
  reportingPeriodEndDate,
  status,
  message,
}) => {
  const isValid = !checkIfBsirExistForReportingPeriodWithGivenStatus(
    data,
    reportingPeriodEndDate,
    status
  );
  return toResult(isValid, message);
};

const recipientManagementCostRule = ({
  data,
  orgIdPerformingWork,
  orgId,
  subprogramFunding,
}) => {
  let isRecipientWithMangementCost = false;
  if (orgId == orgIdPerformingWork) {
    _.forEach(data.children, (child) => {
      if (
        _.get(child, 'additionalInformation.solutionArea') ==
          'MANAGEMENT_AND_ADMINISTRATION' &&
        _.get(child, 'additionalInformation.subProgramFunding') ==
          subprogramFunding.trim()
      ) {
        isRecipientWithMangementCost = true;
      }
    });
  }
  return toResult(isRecipientWithMangementCost);
};

const isRecipientOrganization = ({data, orgIdPerformingWork, orgId}) => {
  let isRecipeint = false;
  if (orgId == orgIdPerformingWork) {
    _.forEach(data.children, (child) => {
      if (
        _.get(child, 'additionalInformation.solutionArea') ==
        'MANAGEMENT_AND_ADMINISTRATION'
      ) {
        isRecipeint = true;
      }
    });
  }
  return toResult(isRecipeint);
};
const isSubRecipientOrganization = ({data, orgIdPerformingWork, orgId}) => {
  let isSubRecipeintOrganization = false;
  if (orgId != orgIdPerformingWork) {
    _.forEach(data.children, (child) => {
      if (
        _.get(child, 'additionalInformation.solutionArea') ==
        'MANAGEMENT_AND_ADMINISTRATION'
      ) {
        isSubRecipeintOrganization = true;
      }
    });
  }
  return toResult(isSubRecipeintOrganization);
};

const hasItems = ({data}) => {
  let hasItems = false;
  if (_.has(data, 'children')) {
    hasItems = true;
  }
  return toResult(hasItems);
};

// In alphabet order
const ruleSet = {
  areCostItemsTheSame,
  arrAttrSumGreaterThan,
  arrAttrSumLessThanOrEqualTo,
  arrAttrSumEqualTo,
  arrAttrRoundSumEqualTo,
  arrSumGreaterThan,
  arrSumLessThanOrEqualTo,
  awardAcceptanceWithFields,
  bcaValidateDocuments,
  managementCostMaxThresholdRule,
  managementCostOPSGForOPSGMaxThresholdRule,
  managementCostSHSPForOPSGMaxThresholdRule,
  managementCostSHSPForSHSPMaxThresholdRule,
  managementCostUASIForUASIMaxThresholdRule,
  managementCostSectionMaxThresholdRule,
  costEstimatePhasedProjectRule,
  compareGrandTotalValue,
  doesNotContainZeroAnnualCost,
  validatePaymentWithAvailableResources,
  compareToMultValidation,
  compareToSumValidation,
  eitherOr,
  hasFundingStreamType,
  hasAtLeastOneOfFundingStreamTypes,
  ifTrueThenCheckedAllOptions,
  ifTrueThenCheckedMinimumOneOption,
  isCheckedMinimumOneOption,
  compareArraySums,
  isCompareValue,
  isCompareSubtractedValue,
  isEmpty,
  isNotEmpty,
  isGreaterThanOrEqualToArrMax,
  isGreaterThanOrEqualToValue,
  isGreaterThanValue,
  isLessThanOrEqualToValue,
  isNotEqual,
  isNotOneOf,
  isOneOf,
  isToggled,
  isPositiveNumber,
  isPositiveWholeNumber,
  isPositiveWholeNumberOrEmpty,
  isRequired,
  isUnique,
  validateProjectName,
  isNotActiveTask,
  lengthEquals,
  maxLength,
  minLength,
  mustBeInRange,
  mustBeInRangeInclusive,
  noDuplicateContactPhoneNumbers,
  noDuplicateEmail,
  nonFederalSources,
  objectListAttributes,
  passthroughMinimumThresholdRule,
  phasedProjectRequired,
  regexMatch,
  requestDetailsCostItemTotal,
  requestDetailsPreAwardV2,
  runCostRules,
  runMultipleLockInAmountsRule,
  runLockInAmountsRule,
  runFundingCategoryTargetsRule,
  runSRMCLimitRule,
  runMultipleSRMCLimitRule,
  runMultipleFundingCategoryTargetsRule,
  runManagementCostFivePercentRule,
  runManagementCostFivePercentRuleProgramIncome,
  managementCostMaxPercentageThresholdRule,
  runManagementCostFivePercentRuleForFilterSubAwards,
  runRuleIf,
  nestedRunRuleIf,
  runRule: runRuleSingle,
  runRules,
  runRulesRequestDetails,
  runRulesRequestDetailsForMultiple,
  runCostShareRulesForMultiple,
  subApplicationValidations,
  sumIsEqualTo,
  validEmail,
  validPhoneExtension,
  validPhoneNumber,
  validateDate,
  validatePOPEndDate,
  validateDateForPOP,
  validateFedShareValuesWithCommitment,
  validateFedShareValuesWithPayments,
  validateFedShareValuesWithPaymentsForSubgrants,
  validateNestedRules,
  validateNestedRulesForArray,
  validateNonFedShareValues,
  validateNoActiveEmployee,
  validateObject,
  validateObjectForArray,
  validateObjectForArrayWithinArray,
  validateActivePersonnelStartDate,
  validatePersonnelStartDate,
  validatePersonnelEndDate,
  runRulesReviewPanel,
  isAnyDuplicateCombination,
  runRulesScoringController,
  validateSingleEntry,
  isFieldEqualInObjectList,
  compareCostItemTotalValue,
  checkIfBsirExistForReportingPeriodWithStatus,
  letpaMinimumThresholdRule,
  recipientManagementCostRule,
  isRecipientOrganization,
  hasItems,
  isSubRecipientOrganization,
  managementCostSectionMaxThresholdRuleSubRecipient,
  isAOorSAOorGMS,
  isAOorSAOorGMSAndROFEnabled,
};

export default ruleSet;
